import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';
import LayoutsList from './case-client/LayoutsList';
import ProcessIntel from './ProcessIntel';

const containerStyle = { display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg-primary)', color: 'var(--text-primary)' };
const headerStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)' };
const bodyStyle = { flex: 1, overflow: 'auto', padding: '20px' };
const btnPrimary = { background: 'var(--accent-color)', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' };
const btnSecondary = { background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px' };
const btnDanger = { background: '#ef444420', color: '#ef4444', border: '1px solid #ef444440', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px' };
const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' };
const cardStyle = { background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px' };
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalContent = { background: 'var(--bg-secondary)', borderRadius: '12px', padding: '24px', width: '600px', maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto', border: '1px solid var(--border-color)' };

/* ─── Field mapping hint (shows how form fields map to case fields) ── */
function FieldMappingHint({ formId, customFields }) {
  const [formFields, setFormFields] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!formId) { setFormFields(null); return; }
    setLoading(true);
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    fetch(`${API_BASE_URL}/xavvy-forms/${formId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        const def = data.definition ? (typeof data.definition === 'string' ? JSON.parse(data.definition) : data.definition) : null;
        if (!def) { setFormFields([]); return; }
        // Recursively extract all named fields from the form definition
        const names = [];
        const walk = (elems) => {
          (elems || []).forEach(e => {
            if (e.name) names.push({ name: e.name, label: e.label || e.name, type: e.type });
            if (e.elements) walk(e.elements);
            if (e.columns) e.columns.forEach(c => walk(c.elements));
            if (e.tabs) e.tabs.forEach(t => walk(t.elements));
          });
        };
        walk(def.elements || []);
        setFormFields(names);
      })
      .catch(() => setFormFields([]))
      .finally(() => setLoading(false));
  }, [formId]);

  if (!formId || loading || !formFields) return null;
  if (formFields.length === 0 && customFields.length === 0) return null;

  // Case-insensitive matching: case field keys are auto-lowercased, form field names may be camelCase
  const caseKeySet = new Set(customFields.map(f => f.key.toLowerCase()));
  const formNameSet = new Set(formFields.map(f => f.name.toLowerCase()));
  const mapped = formFields.filter(f => caseKeySet.has(f.name.toLowerCase()));
  const unmappedForm = formFields.filter(f => !caseKeySet.has(f.name.toLowerCase()));
  const unmappedCase = customFields.filter(f => !formNameSet.has(f.key.toLowerCase()));

  return (
    <div style={{ marginTop: '8px', padding: '8px 10px', background: 'var(--bg-primary)', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: '11px' }}>
      <div style={{ fontWeight: 600, marginBottom: '6px', color: 'var(--text-secondary)' }}>Field Mapping</div>
      <div style={{ color: 'var(--text-tertiary)', marginBottom: '4px' }}>
        Form field <code style={{ background: 'var(--bg-input)', padding: '1px 3px', borderRadius: '3px', fontSize: '10px' }}>name</code> must
        match the case field <code style={{ background: 'var(--bg-input)', padding: '1px 3px', borderRadius: '3px', fontSize: '10px' }}>key</code> for
        values to map automatically.
      </div>
      {mapped.length > 0 && (
        <div style={{ marginTop: '4px' }}>
          {mapped.map(f => {
            const caseField = customFields.find(cf => cf.key.toLowerCase() === f.name.toLowerCase());
            return (
              <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 0' }}>
                <span style={{ color: '#22c55e' }}>✓</span>
                <code style={{ background: 'var(--bg-input)', padding: '1px 4px', borderRadius: '3px', fontSize: '10px', color: 'var(--text-primary)' }}>{f.name}</code>
                <span style={{ color: 'var(--text-tertiary)' }}>→</span>
                <code style={{ background: 'var(--bg-input)', padding: '1px 4px', borderRadius: '3px', fontSize: '10px', color: 'var(--text-primary)' }}>{caseField?.key || f.name}</code>
                <span style={{ color: 'var(--text-tertiary)' }}>({f.label})</span>
              </div>
            );
          })}
        </div>
      )}
      {unmappedForm.length > 0 && (
        <div style={{ marginTop: '4px' }}>
          {unmappedForm.map(f => (
            <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 0' }}>
              <span style={{ color: '#f59e0b' }}>⚠</span>
              <span style={{ color: 'var(--text-secondary)' }}>Form field</span>
              <code style={{ background: 'var(--bg-input)', padding: '1px 4px', borderRadius: '3px', fontSize: '10px' }}>{f.name}</code>
              <span style={{ color: 'var(--text-tertiary)' }}>— no matching case field</span>
            </div>
          ))}
        </div>
      )}
      {unmappedCase.length > 0 && (
        <div style={{ marginTop: '4px' }}>
          {unmappedCase.map(f => (
            <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 0' }}>
              <span style={{ color: '#ef4444' }}>✗</span>
              <span style={{ color: 'var(--text-secondary)' }}>Case field</span>
              <code style={{ background: 'var(--bg-input)', padding: '1px 4px', borderRadius: '3px', fontSize: '10px' }}>{f.key}</code>
              <span style={{ color: 'var(--text-tertiary)' }}>— no matching form field</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Searchable form picker dropdown ─────────────────────────────── */
function FormSearchDropdown({ value, onChange, placeholder, workspaceId }) {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = React.useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    const url = workspaceId
      ? `${API_BASE_URL}/xavvy-forms?workspace_id=${encodeURIComponent(workspaceId)}`
      : `${API_BASE_URL}/xavvy-forms`;
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.json())
      .then(d => setForms(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [workspaceId]);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selected = forms.find(f => f.id === value);
  // Case-manager surfaces (intake, case detail, task detail) only list forms flagged for case-manager use.
  const filtered = forms.filter(f =>
    f.is_case_manager_form && f.name.toLowerCase().includes(query.toLowerCase())
  );

  const handleSelect = (formId) => {
    onChange(formId);
    setQuery('');
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
  };

  const statusColor = (s) => s === 'published' ? '#22c55e' : '#f59e0b';

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen(true)}
        style={{
          ...inputStyle,
          padding: '5px 8px',
          fontSize: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          cursor: 'pointer',
          minHeight: '30px',
        }}
      >
        {open ? (
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            onClick={e => e.stopPropagation()}
            placeholder="Search forms..."
            style={{ border: 'none', background: 'transparent', color: 'var(--text-primary)', outline: 'none', flex: 1, fontSize: '12px', padding: 0 }}
          />
        ) : selected ? (
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selected.name}
            <span style={{ marginLeft: '6px', fontSize: '10px', color: statusColor(selected.status) }}>
              ({selected.status})
            </span>
          </span>
        ) : (
          <span style={{ flex: 1, color: 'var(--text-tertiary)' }}>{placeholder || 'Select a form...'}</span>
        )}
        {value && (
          <span onClick={handleClear} title="Clear" style={{ cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: '14px', lineHeight: 1, padding: '0 2px' }}>×</span>
        )}
        <span style={{ color: 'var(--text-tertiary)', fontSize: '10px' }}>▼</span>
      </div>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 50,
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          borderRadius: '6px',
          marginTop: '2px',
          maxHeight: '200px',
          overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        }}>
          {loading ? (
            <div style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text-tertiary)' }}>Loading forms...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
              {forms.length === 0 ? 'No forms created yet' : 'No matching forms'}
            </div>
          ) : (
            filtered.map(f => (
              <div
                key={f.id}
                onClick={() => handleSelect(f.id)}
                style={{
                  padding: '7px 12px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: f.id === value ? 'var(--bg-tertiary)' : 'transparent',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                onMouseLeave={e => e.currentTarget.style.background = f.id === value ? 'var(--bg-tertiary)' : 'transparent'}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                <span style={{ fontSize: '10px', color: statusColor(f.status), flexShrink: 0, marginLeft: '8px' }}>{f.status}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Email credential picker (filters to email-capable types) ──── */
const EMAIL_CREDENTIAL_TYPES = ['google_account', 'microsoft', 'imap'];
const CRED_TYPE_LABELS = { google_account: 'Gmail', microsoft: 'Microsoft 365', imap: 'IMAP' };

function EmailCredentialDropdown({ value, onChange, onCredentialLoaded }) {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = React.useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    fetch(`${API_BASE_URL}/credentials/`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.json())
      .then(d => {
        const emailCreds = (Array.isArray(d) ? d : []).filter(c => EMAIL_CREDENTIAL_TYPES.includes(c.service_type));
        setCredentials(emailCreds);
        // If a credential is already selected, fire loaded callback with its info
        if (value && onCredentialLoaded) {
          const sel = emailCreds.find(c => c.id === value);
          if (sel) onCredentialLoaded(sel);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selected = credentials.find(c => c.id === value);
  const filtered = credentials.filter(c =>
    c.name.toLowerCase().includes(query.toLowerCase()) ||
    (CRED_TYPE_LABELS[c.service_type] || c.service_type).toLowerCase().includes(query.toLowerCase())
  );

  const handleSelect = (cred) => {
    onChange(cred.id);
    if (onCredentialLoaded) onCredentialLoaded(cred);
    setQuery('');
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
  };

  const typeIcon = (t) => t === 'google_account' ? '📨' : t === 'microsoft' ? '📧' : '📬';

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div
        onClick={() => setOpen(true)}
        style={{
          ...inputStyle,
          padding: '5px 8px',
          fontSize: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          cursor: 'pointer',
          minHeight: '30px',
        }}
      >
        {open ? (
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            onClick={e => e.stopPropagation()}
            placeholder="Search credentials..."
            style={{ border: 'none', background: 'transparent', color: 'var(--text-primary)', outline: 'none', flex: 1, fontSize: '12px', padding: 0 }}
          />
        ) : selected ? (
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {typeIcon(selected.service_type)} {selected.name}
            <span style={{ marginLeft: '6px', fontSize: '10px', color: 'var(--text-tertiary)' }}>
              ({CRED_TYPE_LABELS[selected.service_type] || selected.service_type})
            </span>
          </span>
        ) : (
          <span style={{ flex: 1, color: 'var(--text-tertiary)' }}>Select an email credential...</span>
        )}
        {value && (
          <span onClick={handleClear} title="Clear" style={{ cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: '14px', lineHeight: 1, padding: '0 2px' }}>×</span>
        )}
        <span style={{ color: 'var(--text-tertiary)', fontSize: '10px' }}>▼</span>
      </div>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 50,
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          borderRadius: '6px',
          marginTop: '2px',
          maxHeight: '220px',
          overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        }}>
          {loading ? (
            <div style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text-tertiary)' }}>Loading credentials...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
              {credentials.length === 0
                ? 'No email credentials found. Create a Gmail, Microsoft 365, or IMAP credential first.'
                : 'No matching credentials'}
            </div>
          ) : (
            filtered.map(c => (
              <div
                key={c.id}
                onClick={() => handleSelect(c)}
                style={{
                  padding: '8px 12px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: c.id === value ? 'var(--bg-tertiary)' : 'transparent',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                onMouseLeave={e => e.currentTarget.style.background = c.id === value ? 'var(--bg-tertiary)' : 'transparent'}
              >
                <span style={{ fontSize: '14px' }}>{typeIcon(c.service_type)}</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                <span style={{ fontSize: '10px', color: 'var(--accent-color)', flexShrink: 0 }}>{CRED_TYPE_LABELS[c.service_type] || c.service_type}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Multi-line Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'multi_select', label: 'Multi-Select' },
  { value: 'checkbox', label: 'Checkbox' },
];

const DEFAULT_STATUSES = [
  { key: 'open', label: 'Open', color: '#3b82f6' },
  { key: 'in_progress', label: 'In Progress', color: '#f59e0b' },
  { key: 'under_review', label: 'Under Review', color: '#8b5cf6' },
  { key: 'resolved', label: 'Resolved', color: '#10b981' },
  { key: 'closed', label: 'Closed', color: '#6b7280' },
];

const DEFAULT_TRANSITIONS = {
  open: ['in_progress', 'closed'],
  in_progress: ['under_review', 'resolved', 'open'],
  under_review: ['in_progress', 'resolved'],
  resolved: ['closed', 'in_progress'],
  closed: ['open'],
};

function CaseTypeEditor({ caseType, onSave, onBack, workspaceId, caseTypes: parentCaseTypes, getAuthHeaders }) {
  const [name, setName] = useState(caseType?.name || '');
  const [description, setDescription] = useState(caseType?.description || '');
  const [icon, setIcon] = useState(caseType?.icon || '📋');
  const [caseIdFormat, setCaseIdFormat] = useState(caseType?.case_id_format || 'CASE-{seq6}');

  // Track initial values for unsaved-changes warning
  const initialSnapshot = useRef(null);
  const [statuses, setStatuses] = useState(() => {
    const wf = caseType?.status_workflow_json;
    return wf?.statuses || DEFAULT_STATUSES;
  });
  const [transitions, setTransitions] = useState(() => {
    const wf = caseType?.status_workflow_json;
    return wf?.transitions || DEFAULT_TRANSITIONS;
  });
  const [initial, setInitial] = useState(() => {
    const wf = caseType?.status_workflow_json;
    return wf?.initial || 'open';
  });
  const [terminal, setTerminal] = useState(() => {
    const wf = caseType?.status_workflow_json;
    return wf?.terminal || ['closed'];
  });
  const [customFields, setCustomFields] = useState(() => {
    const schema = caseType?.schema_json;
    return schema?.fields || [];
  });
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [newFieldOptions, setNewFieldOptions] = useState('');
  const [editingFieldIdx, setEditingFieldIdx] = useState(null);
  const [subTab, setSubTab] = useState('general');
  const [newStatusKey, setNewStatusKey] = useState('');
  const [newStatusLabel, setNewStatusLabel] = useState('');
  const [newStatusColor, setNewStatusColor] = useState('#3b82f6');

  // Required documents
  const [requiredDocs, setRequiredDocs] = useState(() => {
    return caseType?.required_documents_json || [];
  });
  const [newDocLabel, setNewDocLabel] = useState('');

  const addRequiredDoc = () => {
    if (!newDocLabel.trim()) return;
    const key = newDocLabel.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    if (requiredDocs.find(d => (d.key || d) === key)) return;
    setRequiredDocs([...requiredDocs, { key, label: newDocLabel.trim() }]);
    setNewDocLabel('');
  };

  const removeRequiredDoc = (idx) => {
    setRequiredDocs(requiredDocs.filter((_, i) => i !== idx));
  };

  // SLA config
  const [slaEnabled, setSlaEnabled] = useState(() => {
    const cfg = caseType?.sla_config_json;
    return !!(cfg && cfg.rules && Object.keys(cfg.rules).length > 0);
  });
  const [slaRules, setSlaRules] = useState(() => {
    const cfg = caseType?.sla_config_json;
    return cfg?.rules || {
      critical: { response_minutes: 30, resolution_minutes: 240 },
      high: { response_minutes: 60, resolution_minutes: 480 },
      medium: { response_minutes: 240, resolution_minutes: 1440 },
      low: { response_minutes: 480, resolution_minutes: 2880 },
    };
  });
  const [slaWarningPct, setSlaWarningPct] = useState(() => {
    const cfg = caseType?.sla_config_json;
    return cfg?.warning_threshold_pct || 80;
  });
  const [slaPauseStatuses, setSlaPauseStatuses] = useState(() => {
    const cfg = caseType?.sla_config_json;
    return (cfg?.pause_statuses || []).join(', ');
  });

  // Phase 5: Form configuration
  const [detailFormId, setDetailFormId] = useState(caseType?.form_config_json?.detail_form_id || '');
  const [intakeFormId, setIntakeFormId] = useState(caseType?.form_config_json?.intake_form_id || '');
  // Phase 5: Email intake (credential-based mailbox polling)
  const [emailIntakeEnabled, setEmailIntakeEnabled] = useState(caseType?.email_intake_json?.enabled || false);
  const [emailCredentialId, setEmailCredentialId] = useState(caseType?.email_intake_json?.credential_id || '');
  const [emailIntakeAddress, setEmailIntakeAddress] = useState(caseType?.email_intake_json?.email_address || caseType?.email_intake_json?.address || '');
  const [emailFolder, setEmailFolder] = useState(caseType?.email_intake_json?.folder || 'INBOX');
  const [emailDefaultPriority, setEmailDefaultPriority] = useState(caseType?.email_intake_json?.default_priority || 'medium');
  const [emailPollInterval, setEmailPollInterval] = useState(caseType?.email_intake_json?.poll_interval_seconds || 60);

  // SLA Escalation
  const [slaEscalation, setSlaEscalation] = useState(caseType?.sla_escalation_json || { on_sla_warning: [], on_sla_breach: [] });

  // Assignment Rules
  const [assignMode, setAssignMode] = useState(caseType?.assignment_rules_json?.mode || 'manual');
  const [assignTeamId, setAssignTeamId] = useState(caseType?.assignment_rules_json?.team_id || '');
  const [assignFallback, setAssignFallback] = useState(caseType?.assignment_rules_json?.fallback || 'unassigned');
  const [assignSkillField, setAssignSkillField] = useState(caseType?.assignment_rules_json?.skill_field || '');
  const [assignRuleSetId, setAssignRuleSetId] = useState(caseType?.assignment_rule_set_id || '');
  const [slaRuleSetId, setSlaRuleSetId] = useState(caseType?.sla_rule_set_id || '');

  // Available rule sets for picker dropdowns
  const [ruleSets, setRuleSets] = useState([]);
  useEffect(() => {
    if (!workspaceId) return;
    axios.get(`${API_BASE_URL}/api/rules/workspaces/${workspaceId}/sets`, getAuthHeaders())
      .then(r => setRuleSets(r.data?.items || r.data || []))
      .catch(() => {});
  }, [workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Capture initial snapshot on mount for dirty checking
  useEffect(() => {
    initialSnapshot.current = JSON.stringify({
      name: caseType?.name || '',
      description: caseType?.description || '',
      icon: caseType?.icon || '📋',
      caseIdFormat: caseType?.case_id_format || 'CASE-{seq6}',
      statuses: caseType?.status_workflow_json?.statuses || DEFAULT_STATUSES,
      transitions: caseType?.status_workflow_json?.transitions || DEFAULT_TRANSITIONS,
      initial: caseType?.status_workflow_json?.initial || 'open',
      terminal: caseType?.status_workflow_json?.terminal || ['closed'],
      customFields: caseType?.schema_json?.fields || [],
      requiredDocs: caseType?.required_documents_json || [],
      slaEnabled: !!(caseType?.sla_config_json?.rules && Object.keys(caseType?.sla_config_json?.rules).length > 0),
      slaRules: caseType?.sla_config_json?.rules || { critical: { response_minutes: 30, resolution_minutes: 240 }, high: { response_minutes: 60, resolution_minutes: 480 }, medium: { response_minutes: 240, resolution_minutes: 1440 }, low: { response_minutes: 480, resolution_minutes: 2880 } },
      slaWarningPct: caseType?.sla_config_json?.warning_threshold_pct || 80,
      slaPauseStatuses: (caseType?.sla_config_json?.pause_statuses || []).join(', '),
      detailFormId: caseType?.form_config_json?.detail_form_id || '',
      intakeFormId: caseType?.form_config_json?.intake_form_id || '',
      emailIntakeEnabled: caseType?.email_intake_json?.enabled || false,
      emailCredentialId: caseType?.email_intake_json?.credential_id || '',
      emailIntakeAddress: caseType?.email_intake_json?.email_address || caseType?.email_intake_json?.address || '',
      emailFolder: caseType?.email_intake_json?.folder || 'INBOX',
      emailDefaultPriority: caseType?.email_intake_json?.default_priority || 'medium',
      emailPollInterval: caseType?.email_intake_json?.poll_interval_seconds || 60,
    });
  }, [caseType]); // eslint-disable-line react-hooks/exhaustive-deps -- re-snapshot after save (parent updates caseType prop with server response)

  const hasUnsavedChanges = () => {
    if (!initialSnapshot.current) return false;
    const current = JSON.stringify({
      name, description, icon, caseIdFormat, statuses, transitions, initial, terminal,
      customFields, requiredDocs, slaEnabled, slaRules, slaWarningPct, slaPauseStatuses,
      detailFormId, intakeFormId, emailIntakeEnabled, emailCredentialId,
      emailIntakeAddress, emailFolder, emailDefaultPriority, emailPollInterval,
    });
    return current !== initialSnapshot.current;
  };

  const handleCancel = () => {
    if (hasUnsavedChanges()) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to discard them?')) return;
    }
    onBack();
  };

  const addField = () => {
    if (!newFieldLabel.trim()) return;
    const key = newFieldLabel.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    if (customFields.find(f => f.key === key)) return;
    const field = { key, label: newFieldLabel.trim(), type: newFieldType, required: newFieldRequired };
    if ((newFieldType === 'dropdown' || newFieldType === 'multi_select') && newFieldOptions.trim()) {
      field.options = newFieldOptions.split(',').map(o => o.trim()).filter(Boolean);
    }
    setCustomFields([...customFields, field]);
    setNewFieldLabel('');
    setNewFieldType('text');
    setNewFieldRequired(false);
    setNewFieldOptions('');
  };

  const removeField = (idx) => {
    setCustomFields(customFields.filter((_, i) => i !== idx));
    if (editingFieldIdx === idx) setEditingFieldIdx(null);
  };

  const moveField = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= customFields.length) return;
    const arr = [...customFields];
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    setCustomFields(arr);
    if (editingFieldIdx === idx) setEditingFieldIdx(target);
    else if (editingFieldIdx === target) setEditingFieldIdx(idx);
  };

  const updateField = (idx, updates) => {
    setCustomFields(customFields.map((f, i) => i === idx ? { ...f, ...updates } : f));
  };

  const addStatus = () => {
    if (!newStatusKey.trim() || !newStatusLabel.trim()) return;
    const key = newStatusKey.trim().toLowerCase().replace(/\s+/g, '_');
    if (statuses.find(s => s.key === key)) return;
    setStatuses([...statuses, { key, label: newStatusLabel.trim(), color: newStatusColor }]);
    setTransitions({ ...transitions, [key]: [] });
    setNewStatusKey('');
    setNewStatusLabel('');
  };

  const removeStatus = (key) => {
    setStatuses(statuses.filter(s => s.key !== key));
    const newTrans = { ...transitions };
    delete newTrans[key];
    Object.keys(newTrans).forEach(k => { newTrans[k] = newTrans[k].filter(t => t !== key); });
    setTransitions(newTrans);
    if (initial === key) setInitial(statuses[0]?.key || '');
    setTerminal(terminal.filter(t => t !== key));
  };

  const toggleTransition = (from, to) => {
    const current = transitions[from] || [];
    if (current.includes(to)) {
      setTransitions({ ...transitions, [from]: current.filter(t => t !== to) });
    } else {
      setTransitions({ ...transitions, [from]: [...current, to] });
    }
  };

  const updateSlaRule = (priority, field, value) => {
    setSlaRules(prev => ({
      ...prev,
      [priority]: { ...prev[priority], [field]: value === '' ? 0 : Number(value) },
    }));
  };

  const handleSave = () => {
    if (!name.trim()) return;
    const existingSchema = caseType?.schema_json || {};
    const data = {
      name: name.trim(),
      description,
      icon,
      case_id_format: caseIdFormat,
      schema_json: { ...existingSchema, fields: customFields },
      status_workflow_json: { statuses, transitions, initial, terminal },
    };
    if (slaEnabled) {
      data.sla_config_json = {
        rules: slaRules,
        warning_threshold_pct: slaWarningPct,
        pause_statuses: slaPauseStatuses.split(',').map(s => s.trim()).filter(Boolean),
      };
    } else {
      data.sla_config_json = null;
    }
    data.required_documents_json = requiredDocs;

    // Phase 5: form config
    if (detailFormId || intakeFormId) {
      data.form_config_json = {};
      if (detailFormId) data.form_config_json.detail_form_id = detailFormId;
      if (intakeFormId) data.form_config_json.intake_form_id = intakeFormId;
    } else {
      data.form_config_json = null;
    }

    // Phase 5: email intake (credential-based polling)
    if (emailIntakeEnabled && emailCredentialId) {
      data.email_intake_json = {
        enabled: true,
        credential_id: emailCredentialId,
        email_address: emailIntakeAddress,
        folder: emailFolder || 'INBOX',
        default_priority: emailDefaultPriority,
        poll_interval_seconds: emailPollInterval || 60,
      };
    } else {
      data.email_intake_json = emailIntakeEnabled ? { enabled: false } : null;
    }

    // SLA Escalation
    const hasEscalation = (slaEscalation.on_sla_warning?.length > 0 || slaEscalation.on_sla_breach?.length > 0);
    data.sla_escalation_json = hasEscalation ? slaEscalation : null;

    // Assignment Rules
    if (assignMode !== 'manual') {
      data.assignment_rules_json = { mode: assignMode, team_id: assignTeamId || undefined, fallback: assignFallback };
      if (assignMode === 'skill_based' && assignSkillField) data.assignment_rules_json.skill_field = assignSkillField;
    } else {
      data.assignment_rules_json = null;
    }
    data.assignment_rule_set_id = assignRuleSetId || null;
    data.sla_rule_set_id = slaRuleSetId || null;

    onSave(data);
  };

  const subTabs = [
    { key: 'general', label: 'General' },
    { key: 'statuses', label: 'Status Workflow' },
    { key: 'config', label: 'Configuration' },
    ...(caseType ? [
      { key: 'stages', label: 'Stages' },
      { key: 'task_types', label: 'Task Types' },
      { key: 'process_intel', label: 'Process Intel' },
    ] : []),
  ];

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={handleCancel} style={{ ...btnSecondary, padding: '6px 12px' }}>← Back</button>
          <h2 style={{ margin: 0, fontSize: '16px' }}>{caseType ? `${icon} ${name || caseType.name}` : 'New Case Type'}</h2>
        </div>
        {(subTab === 'general' || subTab === 'statuses' || subTab === 'config') && (
          <button onClick={handleSave} style={{ ...btnPrimary, opacity: !name.trim() ? 0.5 : 1 }} disabled={!name.trim()}>
            {caseType ? 'Save Changes' : 'Create Case Type'}
          </button>
        )}
      </div>
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)', padding: '0 20px', background: 'var(--bg-secondary)' }}>
        {subTabs.map(tab => (
          <button key={tab.key} onClick={() => setSubTab(tab.key)} style={adminTabStyle(subTab === tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>
      <div style={bodyStyle}>
        {/* ═══ General Tab ═══ */}
        {subTab === 'general' && <div style={{ maxWidth: '800px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Basic info */}
          <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gap: '10px' }}>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Icon</label>
              <input value={icon} onChange={e => setIcon(e.target.value)} style={{ ...inputStyle, textAlign: 'center', fontSize: '18px' }} />
            </div>
            <div>
              <label style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Name *</label>
              <input value={name} onChange={e => setName(e.target.value)} style={inputStyle} placeholder="e.g. Customer Complaint" autoFocus />
            </div>
          </div>

          <div>
            <label style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} />
          </div>

          <div>
            <label style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Case ID Format</label>
            <input value={caseIdFormat} onChange={e => setCaseIdFormat(e.target.value)} style={inputStyle} placeholder="CASE-{seq6}" />
            <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
              Use {'{seq}'} for sequence number, {'{seq4}'} for 4-digit, {'{seq6}'} for 6-digit.
            </div>
          </div>

          {/* Custom Fields */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', display: 'block' }}>Custom Fields</label>

            {/* Existing fields */}
            {customFields.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' }}>
                {customFields.map((f, idx) => (
                  <div key={f.key}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <button onClick={() => moveField(idx, -1)} disabled={idx === 0} style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer', fontSize: '10px', padding: 0, color: idx === 0 ? 'var(--text-tertiary)' : 'var(--text-secondary)', lineHeight: 1 }}>▲</button>
                        <button onClick={() => moveField(idx, 1)} disabled={idx === customFields.length - 1} style={{ background: 'none', border: 'none', cursor: idx === customFields.length - 1 ? 'default' : 'pointer', fontSize: '10px', padding: 0, color: idx === customFields.length - 1 ? 'var(--text-tertiary)' : 'var(--text-secondary)', lineHeight: 1 }}>▼</button>
                      </div>
                      <span style={{ flex: 1, fontSize: '13px' }}>
                        {f.label}
                        <span style={{ color: 'var(--text-tertiary)', fontSize: '11px', marginLeft: '6px' }}>
                          {FIELD_TYPES.find(t => t.value === f.type)?.label || f.type}
                          {f.required ? ' *' : ''}
                        </span>
                      </span>
                      <button onClick={() => setEditingFieldIdx(editingFieldIdx === idx ? null : idx)} style={{ ...btnSecondary, padding: '2px 8px', fontSize: '10px' }}>
                        {editingFieldIdx === idx ? 'Done' : 'Edit'}
                      </button>
                      <button onClick={() => removeField(idx)} style={{ ...btnDanger, padding: '2px 6px', fontSize: '10px' }}>X</button>
                    </div>
                    {/* Inline edit panel */}
                    {editingFieldIdx === idx && (
                      <div style={{ padding: '8px 8px 8px 28px', background: 'var(--bg-tertiary)', borderRadius: '0 0 4px 4px', display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '-2px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                          <div>
                            <label style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Label</label>
                            <input value={f.label} onChange={e => updateField(idx, { label: e.target.value })} style={{ ...inputStyle, fontSize: '12px', padding: '4px 8px' }} />
                          </div>
                          <div>
                            <label style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Type</label>
                            <select value={f.type} onChange={e => updateField(idx, { type: e.target.value })} style={{ ...inputStyle, fontSize: '12px', padding: '4px 8px', cursor: 'pointer' }}>
                              {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                          </div>
                        </div>
                        {(f.type === 'dropdown' || f.type === 'multi_select') && (
                          <div>
                            <label style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Options (comma-separated)</label>
                            <input value={(f.options || []).join(', ')} onChange={e => updateField(idx, { options: e.target.value.split(',').map(o => o.trim()).filter(Boolean) })} style={{ ...inputStyle, fontSize: '12px', padding: '4px 8px' }} placeholder="Option 1, Option 2, Option 3" />
                          </div>
                        )}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                          <input type="checkbox" checked={f.required || false} onChange={e => updateField(idx, { required: e.target.checked })} /> Required
                        </label>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add field */}
            <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '4px' }}>
              <div style={{ flex: 1, minWidth: '140px' }}>
                <label style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Label</label>
                <input value={newFieldLabel} onChange={e => setNewFieldLabel(e.target.value)} style={{ ...inputStyle, fontSize: '12px', padding: '5px 8px' }} placeholder="e.g. Customer Name" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addField())} />
              </div>
              <div style={{ minWidth: '120px' }}>
                <label style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Type</label>
                <select value={newFieldType} onChange={e => setNewFieldType(e.target.value)} style={{ ...inputStyle, fontSize: '12px', padding: '5px 8px', cursor: 'pointer' }}>
                  {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '11px', color: 'var(--text-secondary)', cursor: 'pointer', paddingBottom: '5px' }}>
                <input type="checkbox" checked={newFieldRequired} onChange={e => setNewFieldRequired(e.target.checked)} /> Req
              </label>
              <button onClick={addField} style={{ ...btnSecondary, padding: '5px 12px', fontSize: '12px' }}>Add</button>
            </div>
            {(newFieldType === 'dropdown' || newFieldType === 'multi_select') && (
              <div style={{ marginBottom: '4px' }}>
                <label style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Options (comma-separated)</label>
                <input value={newFieldOptions} onChange={e => setNewFieldOptions(e.target.value)} style={{ ...inputStyle, fontSize: '12px', padding: '5px 8px' }} placeholder="Option 1, Option 2, Option 3" />
              </div>
            )}
            {customFields.length === 0 && (
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }}>No custom fields. Add fields to capture structured data on cases of this type.</div>
            )}
          </div>
        </div>}

        {/* ═══ Status Workflow Tab ═══ */}
        {subTab === 'statuses' && <div style={{ maxWidth: '800px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', display: 'block' }}>Status Workflow (State Machine)</label>

            {/* Status list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
              {statuses.map(s => (
                <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: '13px' }}>{s.label} <span style={{ color: 'var(--text-tertiary)', fontSize: '11px' }}>({s.key})</span></span>
                  <label style={{ fontSize: '10px', display: 'flex', alignItems: 'center', gap: '2px', color: 'var(--text-secondary)' }}>
                    <input type="radio" name="initial" checked={initial === s.key} onChange={() => setInitial(s.key)} /> Initial
                  </label>
                  <label style={{ fontSize: '10px', display: 'flex', alignItems: 'center', gap: '2px', color: 'var(--text-secondary)' }}>
                    <input type="checkbox" checked={terminal.includes(s.key)} onChange={e => {
                      if (e.target.checked) setTerminal([...terminal, s.key]);
                      else setTerminal(terminal.filter(t => t !== s.key));
                    }} /> Terminal
                  </label>
                  <button onClick={() => removeStatus(s.key)} style={{ ...btnDanger, padding: '2px 6px', fontSize: '10px' }}>X</button>
                </div>
              ))}
            </div>

            {/* Add status */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
              <input value={newStatusKey} onChange={e => setNewStatusKey(e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="Key (e.g. escalated)" />
              <input value={newStatusLabel} onChange={e => setNewStatusLabel(e.target.value)} style={{ ...inputStyle, flex: 1 }} placeholder="Label (e.g. Escalated)" />
              <input type="color" value={newStatusColor} onChange={e => setNewStatusColor(e.target.value)} style={{ width: '36px', height: '36px', border: 'none', cursor: 'pointer', borderRadius: '4px' }} />
              <button onClick={addStatus} style={{ ...btnSecondary, padding: '6px 12px', fontSize: '12px' }}>Add</button>
            </div>

            {/* Transitions matrix */}
            <label style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '6px', display: 'block' }}>Allowed Transitions (row → column)</label>
            {statuses.length > 0 && (
              <div style={{ overflow: 'auto', maxWidth: '100%' }}>
                <table style={{ borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '4px 8px', textAlign: 'left' }}>From \ To</th>
                      {statuses.map(s => <th key={s.key} style={{ padding: '4px 6px', textAlign: 'center', color: s.color, fontWeight: 600 }}>{s.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {statuses.map(from => (
                      <tr key={from.key}>
                        <td style={{ padding: '4px 8px', fontWeight: 600, color: from.color }}>{from.label}</td>
                        {statuses.map(to => (
                          <td key={to.key} style={{ padding: '4px 6px', textAlign: 'center' }}>
                            {from.key === to.key ? (
                              <span style={{ color: 'var(--text-tertiary)' }}>—</span>
                            ) : (
                              <input
                                type="checkbox"
                                checked={(transitions[from.key] || []).includes(to.key)}
                                onChange={() => toggleTransition(from.key, to.key)}
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>}

        {/* ═══ Configuration Tab ═══ */}
        {subTab === 'config' && <div style={{ maxWidth: '800px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Required Documents */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', display: 'block' }}>Required Documents</label>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
              Documents that must be attached before a case can be closed.
            </div>
            {requiredDocs.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' }}>
                {requiredDocs.map((rd, idx) => {
                  const label = typeof rd === 'string' ? rd : rd.label || rd.key;
                  return (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', background: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                      <span style={{ fontSize: '13px' }}>📄</span>
                      <span style={{ flex: 1, fontSize: '12px' }}>{label}</span>
                      <button onClick={() => removeRequiredDoc(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '14px', padding: '0 4px' }}>✕</button>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ display: 'flex', gap: '6px' }}>
              <input
                value={newDocLabel}
                onChange={e => setNewDocLabel(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addRequiredDoc()}
                style={{ ...inputStyle, padding: '5px 8px', fontSize: '12px', flex: 1 }}
                placeholder="e.g. Photo Evidence, Police Report"
              />
              <button onClick={addRequiredDoc} style={{ ...btnSecondary, padding: '5px 10px', fontSize: '12px' }} disabled={!newDocLabel.trim()}>+ Add</button>
            </div>
          </div>

          {/* SLA Configuration */}
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>
                <input type="checkbox" checked={slaEnabled} onChange={e => setSlaEnabled(e.target.checked)} style={{ width: '16px', height: '16px' }} />
                SLA Policy
              </label>
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Define response & resolution time targets per priority</span>
            </div>
            {slaEnabled && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr', gap: '6px', fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 }}>
                  <span>Priority</span>
                  <span>Response (min)</span>
                  <span>Resolution (min)</span>
                </div>
                {['critical', 'high', 'medium', 'low'].map(p => (
                  <div key={p} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr', gap: '6px', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', textTransform: 'capitalize', fontWeight: 500 }}>{p}</span>
                    <input
                      type="number" min="0"
                      value={slaRules[p]?.response_minutes ?? ''}
                      onChange={e => updateSlaRule(p, 'response_minutes', e.target.value)}
                      style={{ ...inputStyle, padding: '5px 8px', fontSize: '12px' }}
                      placeholder="minutes"
                    />
                    <input
                      type="number" min="0"
                      value={slaRules[p]?.resolution_minutes ?? ''}
                      onChange={e => updateSlaRule(p, 'resolution_minutes', e.target.value)}
                      style={{ ...inputStyle, padding: '5px 8px', fontSize: '12px' }}
                      placeholder="minutes"
                    />
                  </div>
                ))}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Warning Threshold (%)</label>
                    <input
                      type="number" min="1" max="99"
                      value={slaWarningPct}
                      onChange={e => setSlaWarningPct(Number(e.target.value))}
                      style={{ ...inputStyle, padding: '5px 8px', fontSize: '12px' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Pause SLA in statuses</label>
                    <input
                      value={slaPauseStatuses}
                      onChange={e => setSlaPauseStatuses(e.target.value)}
                      style={{ ...inputStyle, padding: '5px 8px', fontSize: '12px' }}
                      placeholder="e.g. waiting_on_customer"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── SLA Rule Set (overrides static config) ───────────────────── */}
          {ruleSets.length > 0 && (
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>SLA Rule Set <span style={{ fontSize: '11px', fontWeight: 400, color: '#8b5cf6' }}>(Rules Engine)</span></div>
              <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: 0, marginBottom: '8px' }}>
                Optionally delegate SLA deadline computation to a rule set. When set it overrides the static SLA table above.
                The rule set receives <code style={{ fontSize: '10px' }}>case.priority</code> and must output <code style={{ fontSize: '10px' }}>response_minutes</code> and/or <code style={{ fontSize: '10px' }}>resolution_minutes</code>.
              </p>
              <select value={slaRuleSetId} onChange={e => setSlaRuleSetId(e.target.value)} style={{ ...inputStyle, padding: '5px 8px', fontSize: '12px' }}>
                <option value="">None (use static SLA table)</option>
                {ruleSets.map(rs => <option key={rs.id} value={rs.id}>{rs.name}</option>)}
              </select>
            </div>
          )}

          {/* ── SLA Escalation Actions ───────────────────── */}
          {slaEnabled && (
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>SLA Escalation Actions</div>
              <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: 0, marginBottom: '10px' }}>
                Automatic actions when SLA thresholds are hit. Actions execute in order.
              </p>
              {['on_sla_warning', 'on_sla_breach'].map(eventKey => (
                <div key={eventKey} style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: eventKey === 'on_sla_breach' ? '#ef4444' : '#f59e0b' }}>
                    {eventKey === 'on_sla_warning' ? 'On SLA Warning' : 'On SLA Breach'}
                  </div>
                  {(slaEscalation[eventKey] || []).map((act, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '4px' }}>
                      <select value={act.action || ''} onChange={e => setSlaEscalation(prev => { const arr = [...(prev[eventKey] || [])]; arr[idx] = { ...arr[idx], action: e.target.value }; return { ...prev, [eventKey]: arr }; })} style={{ ...inputStyle, flex: 1, padding: '4px 6px', fontSize: '12px' }}>
                        <option value="">Select action...</option>
                        <option value="notify">Notify</option>
                        <option value="escalate_priority">Escalate Priority</option>
                        <option value="reassign">Reassign</option>
                        <option value="fire_workflow">Fire Workflow</option>
                      </select>
                      {act.action === 'notify' && (
                        <select multiple value={act.targets || []} onChange={e => { const vals = [...e.target.selectedOptions].map(o => o.value); setSlaEscalation(prev => { const arr = [...(prev[eventKey] || [])]; arr[idx] = { ...arr[idx], targets: vals }; return { ...prev, [eventKey]: arr }; }); }} style={{ ...inputStyle, flex: 1, padding: '4px 6px', fontSize: '11px', minHeight: '50px' }}>
                          <option value="case_assignee">Case Assignee</option>
                          <option value="team_lead">Team Lead</option>
                          <option value="workspace_admins">Workspace Admins</option>
                        </select>
                      )}
                      {act.action === 'escalate_priority' && (
                        <select value={act.to_priority || ''} onChange={e => setSlaEscalation(prev => { const arr = [...(prev[eventKey] || [])]; arr[idx] = { ...arr[idx], to_priority: e.target.value }; return { ...prev, [eventKey]: arr }; })} style={{ ...inputStyle, flex: 1, padding: '4px 6px', fontSize: '12px' }}>
                          <option value="critical">Critical</option>
                          <option value="high">High</option>
                          <option value="medium">Medium</option>
                        </select>
                      )}
                      {act.action === 'reassign' && (
                        <select value={act.to || ''} onChange={e => setSlaEscalation(prev => { const arr = [...(prev[eventKey] || [])]; arr[idx] = { ...arr[idx], to: e.target.value }; return { ...prev, [eventKey]: arr }; })} style={{ ...inputStyle, flex: 1, padding: '4px 6px', fontSize: '12px' }}>
                          <option value="team_lead">Team Lead</option>
                        </select>
                      )}
                      {act.action === 'fire_workflow' && (
                        <input value={act.workflow_id || ''} onChange={e => setSlaEscalation(prev => { const arr = [...(prev[eventKey] || [])]; arr[idx] = { ...arr[idx], workflow_id: e.target.value }; return { ...prev, [eventKey]: arr }; })} placeholder="Workflow ID" style={{ ...inputStyle, flex: 1, padding: '4px 6px', fontSize: '12px' }} />
                      )}
                      <button onClick={() => setSlaEscalation(prev => ({ ...prev, [eventKey]: prev[eventKey].filter((_, i) => i !== idx) }))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px', padding: '0 4px' }}>&times;</button>
                    </div>
                  ))}
                  <button onClick={() => setSlaEscalation(prev => ({ ...prev, [eventKey]: [...(prev[eventKey] || []), { action: '' }] }))} style={{ ...btnSecondary, padding: '3px 10px', fontSize: '11px' }}>+ Add Action</button>
                </div>
              ))}
            </div>
          )}

          {/* ── Assignment Rules ───────────────────── */}
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>Auto-Assignment</div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginBottom: '3px' }}>Mode</label>
                <select value={assignMode} onChange={e => setAssignMode(e.target.value)} style={{ ...inputStyle, padding: '5px 8px', fontSize: '12px' }}>
                  <option value="manual">Manual (no auto-assign)</option>
                  <option value="round_robin">Round Robin (least loaded)</option>
                  <option value="least_active">Least Active</option>
                  <option value="skill_based">Skill-Based (match field to role)</option>
                </select>
              </div>
              {assignMode !== 'manual' && (
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginBottom: '3px' }}>Fallback</label>
                  <select value={assignFallback} onChange={e => setAssignFallback(e.target.value)} style={{ ...inputStyle, padding: '5px 8px', fontSize: '12px' }}>
                    <option value="unassigned">Leave Unassigned</option>
                    <option value="team_lead">Assign to Team Lead</option>
                  </select>
                </div>
              )}
            </div>
            {assignMode !== 'manual' && assignMode !== 'skill_based' && (
              <div style={{ marginBottom: '8px' }}>
                <label style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginBottom: '3px' }}>Team (optional — scope assignment to this team)</label>
                <input value={assignTeamId} onChange={e => setAssignTeamId(e.target.value)} placeholder="Leave empty for all workspace members" style={{ ...inputStyle, padding: '5px 8px', fontSize: '12px' }} />
              </div>
            )}
            {assignMode === 'skill_based' && (
              <div style={{ marginBottom: '8px' }}>
                <label style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginBottom: '3px' }}>Skill Field (case custom field whose value matches a role name)</label>
                <select value={assignSkillField} onChange={e => setAssignSkillField(e.target.value)} style={{ ...inputStyle, padding: '5px 8px', fontSize: '12px' }}>
                  <option value="">Select field...</option>
                  {customFields.map(f => <option key={f.key} value={f.key}>{f.label || f.key}</option>)}
                </select>
              </div>
            )}
            {/* Rules Engine assignment — takes precedence over the mode above */}
            {ruleSets.length > 0 && (
              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed var(--border-color)' }}>
                <label style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginBottom: '3px' }}>
                  Assignment Rule Set <span style={{ color: '#8b5cf6', fontWeight: 600 }}>(Rules Engine — overrides mode above)</span>
                </label>
                <select value={assignRuleSetId} onChange={e => setAssignRuleSetId(e.target.value)} style={{ ...inputStyle, padding: '5px 8px', fontSize: '12px' }}>
                  <option value="">None (use mode above)</option>
                  {ruleSets.map(rs => <option key={rs.id} value={rs.id}>{rs.name}</option>)}
                </select>
                <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                  Rule set must output <code>assignee_id</code> (user ID integer) to take effect.
                </div>
              </div>
            )}
          </div>

          {/* ── Phase 5: Form Configuration ───────────────────── */}
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>Form Layout (Optional)</div>
            <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: 0, marginBottom: '8px' }}>
              Link xavvy Forms to use as the case detail layout or intake form. Leave empty to use the default field layout.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Detail Form</label>
                <FormSearchDropdown
                  value={detailFormId}
                  onChange={setDetailFormId}
                  placeholder="Default layout (no form)"
                  workspaceId={workspaceId}
                />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Intake Form</label>
                <FormSearchDropdown
                  value={intakeFormId}
                  onChange={setIntakeFormId}
                  placeholder="Default intake (no form)"
                  workspaceId={workspaceId}
                />
              </div>
            </div>
            {detailFormId && (
              <FieldMappingHint formId={detailFormId} customFields={customFields} />
            )}
          </div>

          {/* ── Phase 5: Email-to-Case (Mailbox Polling) ───── */}
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={emailIntakeEnabled} onChange={e => setEmailIntakeEnabled(e.target.checked)} />
                Email-to-Case
              </label>
            </div>
            {emailIntakeEnabled && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: 0 }}>
                  Connect a mailbox to automatically create cases from incoming emails.
                  xavvy will poll the mailbox and convert new emails into cases.
                </p>

                {/* Email credential */}
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' }}>Email Account</label>
                  <EmailCredentialDropdown
                    value={emailCredentialId}
                    onChange={setEmailCredentialId}
                    onCredentialLoaded={(cred) => {
                      // Auto-fill email address from credential data if not already set
                      if (!emailIntakeAddress && cred.data) {
                        const d = typeof cred.data === 'string' ? JSON.parse(cred.data) : cred.data;
                        const addr = d.email || d.username || d.sender || '';
                        if (addr) setEmailIntakeAddress(addr);
                      }
                    }}
                  />
                  <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                    Supports Gmail (Google Account), Microsoft 365, or IMAP credentials.
                    Set up credentials in the <strong>Credential Manager</strong>.
                  </div>
                </div>

                {/* Email address override + folder */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' }}>Mailbox Address</label>
                    <input
                      value={emailIntakeAddress}
                      onChange={e => setEmailIntakeAddress(e.target.value)}
                      style={{ ...inputStyle, padding: '5px 8px', fontSize: '12px' }}
                      placeholder="support@company.com"
                    />
                    <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                      The email address to monitor. Auto-filled from credential if available.
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' }}>Folder</label>
                    <input
                      value={emailFolder}
                      onChange={e => setEmailFolder(e.target.value)}
                      style={{ ...inputStyle, padding: '5px 8px', fontSize: '12px' }}
                      placeholder="INBOX"
                    />
                  </div>
                </div>

                {/* Priority + poll interval */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' }}>Default Priority</label>
                    <select value={emailDefaultPriority} onChange={e => setEmailDefaultPriority(e.target.value)} style={{ ...inputStyle, padding: '5px 8px', fontSize: '12px', cursor: 'pointer' }}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' }}>Poll Interval</label>
                    <select value={emailPollInterval} onChange={e => setEmailPollInterval(Number(e.target.value))} style={{ ...inputStyle, padding: '5px 8px', fontSize: '12px', cursor: 'pointer' }}>
                      <option value={30}>Every 30 seconds</option>
                      <option value={60}>Every 1 minute</option>
                      <option value={120}>Every 2 minutes</option>
                      <option value={300}>Every 5 minutes</option>
                      <option value={600}>Every 10 minutes</option>
                    </select>
                  </div>
                </div>

                {/* How it works info box */}
                <div style={{
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  padding: '10px 12px',
                  fontSize: '11px',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.5',
                }}>
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>How it works</div>
                  <ul style={{ margin: 0, paddingLeft: '16px' }}>
                    <li>xavvy polls the connected mailbox at the configured interval for new unread emails.</li>
                    <li>Each new email creates a case with the subject as the title and body as description.</li>
                    <li>Replies containing <code style={{ background: 'var(--bg-input)', padding: '1px 4px', borderRadius: '3px', fontSize: '10px' }}>[CASE-NUMBER]</code> in the subject are threaded as comments on the existing case.</li>
                    <li>Processed emails are marked as read. Auto-assignment and SLA rules apply automatically.</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

        </div>}

        {/* ═══ Stages Tab ═══ */}
        {subTab === 'stages' && caseType && (
          <StagesAdmin workspaceId={workspaceId} caseTypeId={caseType.id} caseTypes={parentCaseTypes || []} getAuthHeaders={getAuthHeaders} />
        )}

        {/* ═══ Task Types Tab ═══ */}
        {subTab === 'task_types' && caseType && (
          <TaskTypesAdmin workspaceId={workspaceId} caseTypeId={caseType.id} caseTypes={parentCaseTypes || []} getAuthHeaders={getAuthHeaders} caseTypeSchema={caseType?.schema_json} />
        )}

        {/* ═══ Process Intelligence Tab (§4.96) ═══ */}
        {subTab === 'process_intel' && caseType && (
          <ProcessIntel sourceType="case" sourceId={caseType.id} sourceName={caseType.name} embedded />
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Teams Management
// ═══════════════════════════════════════════════════════════════════════════
function TeamsAdmin({ workspaceId }) {
  const getAuth = useCallback(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  const [teams, setTeams] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTeam, setEditingTeam] = useState(null); // null | 'new' | team obj
  const [teamName, setTeamName] = useState('');
  const [teamDesc, setTeamDesc] = useState('');
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [addMemberUserId, setAddMemberUserId] = useState('');
  const [addMemberRole, setAddMemberRole] = useState('member');

  const load = useCallback(async () => {
    try {
      const [tRes, mRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/teams`, getAuth()),
        axios.get(`${API_BASE_URL}/workspaces/${workspaceId}`, getAuth()),
      ]);
      setTeams(tRes.data || []);
      setMembers(mRes.data?.members || []);
    } catch { setTeams([]); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const handleSaveTeam = async () => {
    if (!teamName.trim()) return;
    try {
      if (editingTeam && editingTeam !== 'new') {
        await axios.patch(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/teams/${editingTeam.id}`, { name: teamName, description: teamDesc }, getAuth());
      } else {
        await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/teams`, { name: teamName, description: teamDesc }, getAuth());
      }
      setEditingTeam(null);
      setTeamName('');
      setTeamDesc('');
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save team'); }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!confirm('Delete this team? Members will not be removed from the workspace.')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/teams/${teamId}`, getAuth());
      if (expandedTeam === teamId) setExpandedTeam(null);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete team'); }
  };

  const handleExpandTeam = async (teamId) => {
    if (expandedTeam === teamId) { setExpandedTeam(null); return; }
    try {
      const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/teams/${teamId}`, getAuth());
      setTeams(prev => prev.map(t => t.id === teamId ? res.data : t));
      setExpandedTeam(teamId);
    } catch { /* ignore */ }
  };

  const handleAddMember = async (teamId) => {
    if (!addMemberUserId) return;
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/teams/${teamId}/members`, { user_id: Number(addMemberUserId), role: addMemberRole }, getAuth());
      setAddMemberUserId('');
      setAddMemberRole('member');
      // Refresh team
      const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/teams/${teamId}`, getAuth());
      setTeams(prev => prev.map(t => t.id === teamId ? res.data : t));
    } catch (err) { alert(err.response?.data?.detail || 'Failed to add member'); }
  };

  const handleRemoveMember = async (teamId, userId) => {
    if (!confirm('Remove this member from the team?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/teams/${teamId}/members/${userId}`, getAuth());
      const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/teams/${teamId}`, getAuth());
      setTeams(prev => prev.map(t => t.id === teamId ? res.data : t));
    } catch (err) { alert(err.response?.data?.detail || 'Failed to remove member'); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Loading...</div>;

  return (
    <div>
      {/* Create / Edit form */}
      {editingTeam ? (
        <div style={{ ...cardStyle, marginBottom: '16px', borderColor: 'var(--accent-color)' }}>
          <h4 style={{ margin: '0 0 12px', fontSize: '14px' }}>{editingTeam === 'new' ? 'Create Team' : 'Edit Team'}</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <input value={teamName} onChange={e => setTeamName(e.target.value)} style={inputStyle} placeholder="Team name *" autoFocus />
            <input value={teamDesc} onChange={e => setTeamDesc(e.target.value)} style={inputStyle} placeholder="Description (optional)" />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setEditingTeam(null); setTeamName(''); setTeamDesc(''); }} style={btnSecondary}>Cancel</button>
              <button onClick={handleSaveTeam} style={btnPrimary} disabled={!teamName.trim()}>
                {editingTeam === 'new' ? 'Create' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button onClick={() => setEditingTeam('new')} style={{ ...btnPrimary, marginBottom: '16px' }}>+ New Team</button>
      )}

      {teams.length === 0 && !editingTeam && (
        <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)', fontSize: '13px' }}>No teams yet. Create a team to organize your case workers.</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {teams.map(t => (
          <div key={t.id} style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => handleExpandTeam(t.id)}>
                <div style={{ fontWeight: 600, fontSize: '14px' }}>{t.name}</div>
                {t.description && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>{t.description}</div>}
                <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }}>{t.member_count || 0} member{t.member_count !== 1 ? 's' : ''}</div>
              </div>
              <button onClick={() => { setEditingTeam(t); setTeamName(t.name); setTeamDesc(t.description || ''); }} style={{ ...btnSecondary, padding: '5px 10px', fontSize: '12px' }}>Edit</button>
              <button onClick={() => handleDeleteTeam(t.id)} style={{ ...btnDanger, padding: '5px 10px' }}>Delete</button>
              <button onClick={() => handleExpandTeam(t.id)} style={{ ...btnSecondary, padding: '5px 10px', fontSize: '12px' }}>
                {expandedTeam === t.id ? 'Collapse' : 'Members'}
              </button>
            </div>

            {/* Expanded member management */}
            {expandedTeam === t.id && (
              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border-color)' }}>
                {/* Add member */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'center' }}>
                  <select value={addMemberUserId} onChange={e => setAddMemberUserId(e.target.value)} style={{ ...inputStyle, flex: 1, cursor: 'pointer' }}>
                    <option value="">Select user to add...</option>
                    {members
                      .filter(m => !(t.members || []).some(tm => tm.user_id === m.user_id))
                      .map(m => <option key={m.user_id} value={m.user_id}>{m.username}</option>)}
                  </select>
                  <select value={addMemberRole} onChange={e => setAddMemberRole(e.target.value)} style={{ ...inputStyle, width: '100px', cursor: 'pointer' }}>
                    <option value="member">Member</option>
                    <option value="lead">Lead</option>
                  </select>
                  <button onClick={() => handleAddMember(t.id)} style={{ ...btnPrimary, padding: '6px 12px', fontSize: '12px', whiteSpace: 'nowrap' }} disabled={!addMemberUserId}>+ Add</button>
                </div>
                {/* Member list */}
                {(t.members || []).length === 0 ? (
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', padding: '8px 0' }}>No members. Add workspace users to this team.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {(t.members || []).map(m => (
                      <div key={m.user_id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 8px', borderRadius: '6px', background: 'var(--bg-tertiary)' }}>
                        <span style={{ fontSize: '13px', flex: 1 }}>{m.username || `User ${m.user_id}`}</span>
                        <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: m.role === 'lead' ? '#f59e0b20' : 'var(--bg-secondary)', color: m.role === 'lead' ? '#f59e0b' : 'var(--text-secondary)', fontWeight: 500 }}>{m.role}</span>
                        <button onClick={() => handleRemoveMember(t.id, m.user_id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '12px', padding: '2px', opacity: 0.6 }} title="Remove">✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Business Calendars
// ═══════════════════════════════════════════════════════════════════════════
function BusinessCalendarsAdmin({ workspaceId }) {
  const getAuth = useCallback(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | calendar obj
  const [form, setForm] = useState({ name: '', timezone: 'UTC', working_hours: { start: '09:00', end: '17:00', days: [1, 2, 3, 4, 5] }, holidays: [] });
  const [newHoliday, setNewHoliday] = useState({ date: '', name: '' });

  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/business-calendars`, getAuth());
      setCalendars(res.data || []);
    } catch { setCalendars([]); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (cal) => {
    if (cal === 'new') {
      setForm({ name: '', timezone: 'UTC', working_hours: { start: '09:00', end: '17:00', days: [1, 2, 3, 4, 5] }, holidays: [] });
    } else {
      const wh = cal.working_hours_json || {};
      const hols = cal.holidays_json || [];
      setForm({ name: cal.name, timezone: cal.timezone || 'UTC', working_hours: { start: wh.start || '09:00', end: wh.end || '17:00', days: wh.days || [1, 2, 3, 4, 5] }, holidays: hols });
    }
    setEditing(cal);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    const body = {
      name: form.name,
      timezone: form.timezone,
      working_hours_json: form.working_hours,
      holidays_json: form.holidays,
    };
    try {
      if (editing && editing !== 'new') {
        await axios.patch(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/business-calendars/${editing.id}`, body, getAuth());
      } else {
        await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/business-calendars`, body, getAuth());
      }
      setEditing(null);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save calendar'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this business calendar?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/business-calendars/${id}`, getAuth());
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete calendar'); }
  };

  const toggleDay = (dayIdx) => {
    setForm(prev => {
      const days = prev.working_hours.days.includes(dayIdx)
        ? prev.working_hours.days.filter(d => d !== dayIdx)
        : [...prev.working_hours.days, dayIdx].sort();
      return { ...prev, working_hours: { ...prev.working_hours, days } };
    });
  };

  const addHoliday = () => {
    if (!newHoliday.date || !newHoliday.name.trim()) return;
    setForm(prev => ({ ...prev, holidays: [...prev.holidays, { ...newHoliday }] }));
    setNewHoliday({ date: '', name: '' });
  };

  const removeHoliday = (idx) => {
    setForm(prev => ({ ...prev, holidays: prev.holidays.filter((_, i) => i !== idx) }));
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Loading...</div>;

  return (
    <div>
      {editing ? (
        <div style={{ ...cardStyle, marginBottom: '16px', borderColor: 'var(--accent-color)' }}>
          <h4 style={{ margin: '0 0 12px', fontSize: '14px' }}>{editing === 'new' ? 'Create Business Calendar' : 'Edit Calendar'}</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} placeholder="e.g., US Business Hours" autoFocus />
            </div>
            <div>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Timezone</label>
              <select value={form.timezone} onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                {['UTC', 'US/Eastern', 'US/Central', 'US/Mountain', 'US/Pacific', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Kolkata', 'Australia/Sydney'].map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Working Hours</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="time" value={form.working_hours.start} onChange={e => setForm(f => ({ ...f, working_hours: { ...f.working_hours, start: e.target.value } }))} style={{ ...inputStyle, width: '130px' }} />
                <span style={{ color: 'var(--text-secondary)' }}>to</span>
                <input type="time" value={form.working_hours.end} onChange={e => setForm(f => ({ ...f, working_hours: { ...f.working_hours, end: e.target.value } }))} style={{ ...inputStyle, width: '130px' }} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Working Days</label>
              <div style={{ display: 'flex', gap: '4px' }}>
                {DAYS.map((d, i) => (
                  <button key={i} onClick={() => toggleDay(i)} style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', border: '1px solid var(--border-color)', background: form.working_hours.days.includes(i) ? 'var(--accent-color)' : 'var(--bg-tertiary)', color: form.working_hours.days.includes(i) ? '#fff' : 'var(--text-secondary)' }}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Holidays</label>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                <input type="date" value={newHoliday.date} onChange={e => setNewHoliday(h => ({ ...h, date: e.target.value }))} style={{ ...inputStyle, width: '160px' }} />
                <input value={newHoliday.name} onChange={e => setNewHoliday(h => ({ ...h, name: e.target.value }))} style={{ ...inputStyle, flex: 1 }} placeholder="Holiday name" />
                <button onClick={addHoliday} style={{ ...btnSecondary, padding: '6px 10px', fontSize: '12px' }} disabled={!newHoliday.date || !newHoliday.name.trim()}>+</button>
              </div>
              {form.holidays.map((h, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '4px 0', fontSize: '12px' }}>
                  <span style={{ color: 'var(--text-secondary)', minWidth: '100px' }}>{h.date}</span>
                  <span style={{ flex: 1 }}>{h.name}</span>
                  <button onClick={() => removeHoliday(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '12px' }}>✕</button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setEditing(null)} style={btnSecondary}>Cancel</button>
              <button onClick={handleSave} style={btnPrimary} disabled={!form.name.trim()}>
                {editing === 'new' ? 'Create' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button onClick={() => startEdit('new')} style={{ ...btnPrimary, marginBottom: '16px' }}>+ New Calendar</button>
      )}

      {calendars.length === 0 && !editing && (
        <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)', fontSize: '13px' }}>No business calendars. Define working hours and holidays for SLA calculations.</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {calendars.map(cal => (
          <div key={cal.id} style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '14px' }}>{cal.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                  {cal.timezone || 'UTC'} | {cal.working_hours_json?.start || '09:00'}-{cal.working_hours_json?.end || '17:00'} | {(cal.working_hours_json?.days || []).map(d => DAYS[d]).join(', ')}
                </div>
                {cal.holidays_json?.length > 0 && (
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>{cal.holidays_json.length} holiday{cal.holidays_json.length !== 1 ? 's' : ''}</div>
                )}
              </div>
              <button onClick={() => startEdit(cal)} style={{ ...btnSecondary, padding: '5px 10px', fontSize: '12px' }}>Edit</button>
              <button onClick={() => handleDelete(cal.id)} style={{ ...btnDanger, padding: '5px 10px' }}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Notification Preferences
// ═══════════════════════════════════════════════════════════════════════════
function NotificationPrefsAdmin({ workspaceId }) {
  const getAuth = useCallback(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  const [prefs, setPrefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const EVENT_TYPES = [
    { key: 'case_assigned', label: 'Case assigned to me' },
    { key: 'case_updated', label: 'Case I watch is updated' },
    { key: 'case_commented', label: 'New comment on my case' },
    { key: 'case_mentioned', label: '@mentioned in a comment' },
    { key: 'sla_warning', label: 'SLA approaching deadline' },
    { key: 'sla_breach', label: 'SLA breached' },
    { key: 'task_assigned', label: 'Task assigned to me' },
    { key: 'task_due', label: 'Task approaching due date' },
    { key: 'case_escalated', label: 'Case escalated' },
  ];

  const CHANNELS = ['in_app', 'email'];

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/notification-preferences`, getAuth())
      .then(res => {
        setPrefs(res.data || []);
        // Initialize defaults for missing event types
        const existing = new Set((res.data || []).map(p => `${p.event_type}:${p.channel}`));
        const defaults = [];
        for (const evt of EVENT_TYPES) {
          for (const ch of CHANNELS) {
            if (!existing.has(`${evt.key}:${ch}`)) {
              defaults.push({ event_type: evt.key, channel: ch, enabled: true });
            }
          }
        }
        if (defaults.length > 0) setPrefs(prev => [...prev, ...defaults]);
      })
      .catch(() => {
        // Initialize all defaults
        const defaults = [];
        for (const evt of EVENT_TYPES) {
          for (const ch of CHANNELS) {
            defaults.push({ event_type: evt.key, channel: ch, enabled: true });
          }
        }
        setPrefs(defaults);
      })
      .finally(() => setLoading(false));
  }, [workspaceId]);

  const togglePref = (eventType, channel) => {
    setPrefs(prev => prev.map(p =>
      p.event_type === eventType && p.channel === channel ? { ...p, enabled: !p.enabled } : p
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/notification-preferences`, prefs, getAuth());
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save preferences'); }
    setSaving(false);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Loading...</div>;

  const getPref = (eventType, channel) => {
    const p = prefs.find(pr => pr.event_type === eventType && pr.channel === channel);
    return p ? p.enabled : true;
  };

  return (
    <div>
      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>Choose which events trigger notifications and how you receive them.</p>
      <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase' }}>Event</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase', width: '80px' }}>In-App</th>
              <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '11px', textTransform: 'uppercase', width: '80px' }}>Email</th>
            </tr>
          </thead>
          <tbody>
            {EVENT_TYPES.map(evt => (
              <tr key={evt.key} style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '10px 14px' }}>{evt.label}</td>
                {CHANNELS.map(ch => (
                  <td key={ch} style={{ padding: '10px 14px', textAlign: 'center' }}>
                    <input type="checkbox" checked={getPref(evt.key, ch)} onChange={() => togglePref(evt.key, ch)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={handleSave} style={btnPrimary} disabled={saving}>
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Decision Tables
// ═══════════════════════════════════════════════════════════════════════════
function DecisionTablesAdmin({ workspaceId }) {
  const getAuth = useCallback(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | table obj
  const [form, setForm] = useState({ name: '', description: '', match_mode: 'first', input_columns: [], output_columns: [], rules: [] });
  const [newInputCol, setNewInputCol] = useState({ key: '', label: '', type: 'field' });
  const [newOutputCol, setNewOutputCol] = useState({ key: '', label: '', type: 'set_field' });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/decision-tables`, getAuth());
      setTables(res.data || []);
    } catch { setTables([]); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (dt) => {
    if (dt === 'new') {
      setForm({ name: '', description: '', match_mode: 'first', input_columns: [], output_columns: [], rules: [] });
    } else {
      setForm({
        name: dt.name,
        description: dt.description || '',
        match_mode: dt.match_mode || 'first',
        input_columns: dt.input_columns_json || [],
        output_columns: dt.output_columns_json || [],
        rules: dt.rules_json || [],
      });
    }
    setEditing(dt);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    const body = {
      name: form.name,
      description: form.description,
      match_mode: form.match_mode,
      input_columns_json: form.input_columns,
      output_columns_json: form.output_columns,
      rules_json: form.rules,
    };
    try {
      if (editing && editing !== 'new') {
        await axios.patch(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/decision-tables/${editing.id}`, body, getAuth());
      } else {
        await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/decision-tables`, body, getAuth());
      }
      setEditing(null);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save decision table'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this decision table?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/decision-tables/${id}`, getAuth());
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  const addInputCol = () => {
    if (!newInputCol.key.trim() || !newInputCol.label.trim()) return;
    setForm(f => ({ ...f, input_columns: [...f.input_columns, { ...newInputCol }] }));
    setNewInputCol({ key: '', label: '', type: 'field' });
  };

  const addOutputCol = () => {
    if (!newOutputCol.key.trim() || !newOutputCol.label.trim()) return;
    setForm(f => ({ ...f, output_columns: [...f.output_columns, { ...newOutputCol }] }));
    setNewOutputCol({ key: '', label: '', type: 'set_field' });
  };

  const removeCol = (arr, idx) => arr.filter((_, i) => i !== idx);

  const addRule = () => {
    const inputs = {};
    const outputs = {};
    form.input_columns.forEach(c => { inputs[c.key] = ''; });
    form.output_columns.forEach(c => { outputs[c.key] = ''; });
    setForm(f => ({ ...f, rules: [...f.rules, { inputs, outputs }] }));
  };

  const updateRuleCell = (ruleIdx, section, key, value) => {
    setForm(f => ({
      ...f,
      rules: f.rules.map((r, i) =>
        i === ruleIdx ? { ...r, [section]: { ...r[section], [key]: value } } : r
      ),
    }));
  };

  const removeRule = (idx) => {
    setForm(f => ({ ...f, rules: f.rules.filter((_, i) => i !== idx) }));
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Loading...</div>;

  if (editing) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h4 style={{ margin: 0, fontSize: '15px' }}>{editing === 'new' ? 'Create Decision Table' : 'Edit Decision Table'}</h4>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setEditing(null)} style={btnSecondary}>Cancel</button>
            <button onClick={handleSave} style={btnPrimary} disabled={!form.name.trim()}>Save</button>
          </div>
        </div>

        {/* Basic info */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Name *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} placeholder="e.g., Priority Router" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Match Mode</label>
            <select value={form.match_mode} onChange={e => setForm(f => ({ ...f, match_mode: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="first">First match (stop at first matching rule)</option>
              <option value="all">All matches (execute all matching rules)</option>
            </select>
          </div>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Description</label>
          <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={inputStyle} placeholder="What does this table do?" />
        </div>

        {/* Column definitions */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          {/* Input columns */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '8px', color: '#3b82f6' }}>Input Conditions (IF)</div>
            {form.input_columns.map((col, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center', padding: '4px 0', fontSize: '12px' }}>
                <code style={{ color: 'var(--accent-color)', minWidth: '80px' }}>{col.key}</code>
                <span style={{ flex: 1 }}>{col.label}</span>
                <span style={{ color: 'var(--text-tertiary)', fontSize: '11px' }}>{col.type}</span>
                <button onClick={() => setForm(f => ({ ...f, input_columns: removeCol(f.input_columns, idx) }))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '11px' }}>✕</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
              <input value={newInputCol.key} onChange={e => setNewInputCol(c => ({ ...c, key: e.target.value }))} style={{ ...inputStyle, width: '80px', padding: '4px 6px', fontSize: '11px' }} placeholder="key" />
              <input value={newInputCol.label} onChange={e => setNewInputCol(c => ({ ...c, label: e.target.value }))} style={{ ...inputStyle, flex: 1, padding: '4px 6px', fontSize: '11px' }} placeholder="Label" />
              <select value={newInputCol.type} onChange={e => setNewInputCol(c => ({ ...c, type: e.target.value }))} style={{ ...inputStyle, width: '80px', padding: '4px 6px', fontSize: '11px', cursor: 'pointer' }}>
                <option value="field">Field</option>
                <option value="expression">Expr</option>
              </select>
              <button onClick={addInputCol} style={{ ...btnSecondary, padding: '4px 8px', fontSize: '11px' }} disabled={!newInputCol.key || !newInputCol.label}>+</button>
            </div>
          </div>

          {/* Output columns */}
          <div style={cardStyle}>
            <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '8px', color: '#10b981' }}>Output Actions (THEN)</div>
            {form.output_columns.map((col, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center', padding: '4px 0', fontSize: '12px' }}>
                <code style={{ color: 'var(--accent-color)', minWidth: '80px' }}>{col.key}</code>
                <span style={{ flex: 1 }}>{col.label}</span>
                <span style={{ color: 'var(--text-tertiary)', fontSize: '11px' }}>{col.type}</span>
                <button onClick={() => setForm(f => ({ ...f, output_columns: removeCol(f.output_columns, idx) }))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '11px' }}>✕</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
              <input value={newOutputCol.key} onChange={e => setNewOutputCol(c => ({ ...c, key: e.target.value }))} style={{ ...inputStyle, width: '80px', padding: '4px 6px', fontSize: '11px' }} placeholder="key" />
              <input value={newOutputCol.label} onChange={e => setNewOutputCol(c => ({ ...c, label: e.target.value }))} style={{ ...inputStyle, flex: 1, padding: '4px 6px', fontSize: '11px' }} placeholder="Label" />
              <select value={newOutputCol.type} onChange={e => setNewOutputCol(c => ({ ...c, type: e.target.value }))} style={{ ...inputStyle, width: '100px', padding: '4px 6px', fontSize: '11px', cursor: 'pointer' }}>
                <option value="set_field">Set Field</option>
                <option value="set_priority">Set Priority</option>
                <option value="set_status">Set Status</option>
                <option value="assign">Assign</option>
                <option value="trigger_workflow">Trigger Workflow</option>
              </select>
              <button onClick={addOutputCol} style={{ ...btnSecondary, padding: '4px 8px', fontSize: '11px' }} disabled={!newOutputCol.key || !newOutputCol.label}>+</button>
            </div>
          </div>
        </div>

        {/* Rules table */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontWeight: 600, fontSize: '13px' }}>Rules ({form.rules.length})</span>
            <button onClick={addRule} style={{ ...btnSecondary, padding: '4px 10px', fontSize: '12px' }} disabled={form.input_columns.length === 0 && form.output_columns.length === 0}>+ Add Rule</button>
          </div>
          {form.rules.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)', fontSize: '13px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
              Define input and output columns, then add rules.
            </div>
          ) : (
            <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-secondary)' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'center', fontSize: '10px', color: 'var(--text-tertiary)', width: '30px' }}>#</th>
                    {form.input_columns.map(c => (
                      <th key={`i-${c.key}`} style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', textTransform: 'uppercase', color: '#3b82f6', borderBottom: '2px solid #3b82f6' }}>{c.label}</th>
                    ))}
                    {form.output_columns.map(c => (
                      <th key={`o-${c.key}`} style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', textTransform: 'uppercase', color: '#10b981', borderBottom: '2px solid #10b981' }}>{c.label}</th>
                    ))}
                    <th style={{ width: '30px' }} />
                  </tr>
                </thead>
                <tbody>
                  {form.rules.map((rule, rIdx) => (
                    <tr key={rIdx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '4px 8px', textAlign: 'center', color: 'var(--text-tertiary)' }}>{rIdx + 1}</td>
                      {form.input_columns.map(c => (
                        <td key={`i-${c.key}`} style={{ padding: '2px 4px' }}>
                          <input value={rule.inputs?.[c.key] || ''} onChange={e => updateRuleCell(rIdx, 'inputs', c.key, e.target.value)} style={{ ...inputStyle, padding: '3px 6px', fontSize: '11px', background: '#3b82f608' }} placeholder="any" />
                        </td>
                      ))}
                      {form.output_columns.map(c => (
                        <td key={`o-${c.key}`} style={{ padding: '2px 4px' }}>
                          <input value={rule.outputs?.[c.key] || ''} onChange={e => updateRuleCell(rIdx, 'outputs', c.key, e.target.value)} style={{ ...inputStyle, padding: '3px 6px', fontSize: '11px', background: '#10b98108' }} placeholder="—" />
                        </td>
                      ))}
                      <td style={{ padding: '2px 4px' }}>
                        <button onClick={() => removeRule(rIdx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '11px' }}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => startEdit('new')} style={{ ...btnPrimary, marginBottom: '16px' }}>+ New Decision Table</button>

      {tables.length === 0 && (
        <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)', fontSize: '13px' }}>No decision tables. Create if-then rules to auto-route, assign, or classify cases.</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {tables.map(dt => (
          <div key={dt.id} style={{ ...cardStyle, opacity: dt.is_active ? 1 : 0.5 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '14px' }}>{dt.name}</div>
                {dt.description && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>{dt.description}</div>}
                <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px', display: 'flex', gap: '12px' }}>
                  <span>{(dt.input_columns_json || []).length} input{(dt.input_columns_json || []).length !== 1 ? 's' : ''}</span>
                  <span>{(dt.output_columns_json || []).length} output{(dt.output_columns_json || []).length !== 1 ? 's' : ''}</span>
                  <span>{(dt.rules_json || []).length} rule{(dt.rules_json || []).length !== 1 ? 's' : ''}</span>
                  <span>v{dt.version}</span>
                  <span>{dt.match_mode === 'first' ? 'First match' : 'All matches'}</span>
                </div>
              </div>
              {!dt.is_active && <span style={{ background: '#ef444420', color: '#ef4444', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 600 }}>Inactive</span>}
              <button onClick={() => startEdit(dt)} style={{ ...btnSecondary, padding: '5px 10px', fontSize: '12px' }}>Edit</button>
              <button onClick={() => handleDelete(dt.id)} style={{ ...btnDanger, padding: '5px 10px' }}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Task Types Admin (Phase 7)
// ═══════════════════════════════════════════════════════════════════════════
function TaskTypesAdmin({ workspaceId, caseTypes, getAuthHeaders, caseTypeId: propCaseTypeId, caseTypeSchema }) {
  const [selectedCT, setSelectedCT] = useState(propCaseTypeId || '');
  const [taskTypes, setTaskTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | task type obj
  const [form, setForm] = useState({});
  const [workflows, setWorkflows] = useState([]);
  const [ctStages, setCtStages] = useState([]);

  useEffect(() => { if (propCaseTypeId) setSelectedCT(propCaseTypeId); }, [propCaseTypeId]);

  useEffect(() => {
    if (!workspaceId) return;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/workflows/`, { params: { workspace_id: workspaceId }, ...getAuthHeaders() });
        setWorkflows(Array.isArray(res.data) ? res.data : []);
      } catch { setWorkflows([]); }
    })();
  }, [workspaceId]);

  const load = useCallback(async () => {
    if (!workspaceId || !selectedCT) { setTaskTypes([]); setCtStages([]); return; }
    setLoading(true);
    try {
      const [ttRes, stgRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types/${selectedCT}/task-types`, getAuthHeaders()),
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types/${selectedCT}/stages`, getAuthHeaders()),
      ]);
      setTaskTypes(ttRes.data || []);
      setCtStages(Array.isArray(stgRes.data) ? stgRes.data : []);
    } catch { setTaskTypes([]); setCtStages([]); }
    setLoading(false);
  }, [workspaceId, selectedCT]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (tt) => {
    setEditing(tt || 'new');
    const triggerEventAliases = { created: 'case_created' };
    setForm(tt ? { ...tt, trigger_event: triggerEventAliases[tt.trigger_event] || tt.trigger_event || '', preconditions_json: tt?.preconditions_json || { conditions: [], match: 'all' }, trigger_event_config_json: tt?.trigger_event_config_json || {} } : { name: '', description: '', launch_type: 'required', default_priority: 'medium', default_assignment: 'case_assignee', workflow_id: '', sla_minutes: '', stage_id: '', icon: 'checkbox', preconditions_json: { conditions: [], match: 'all' }, deadline_action: '', required_for_completion: false, instructions: '', estimated_duration_minutes: '', trigger_event: '', trigger_event_config_json: {}, max_instances: 1 });
  };

  const handleSave = async () => {
    if (!form.name?.trim()) { alert('Name is required'); return; }
    try {
      const payload = { ...form, sla_minutes: form.sla_minutes ? parseInt(form.sla_minutes) : null };
      if (editing === 'new') {
        await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types/${selectedCT}/task-types`, payload, getAuthHeaders());
      } else {
        await axios.patch(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types/${selectedCT}/task-types/${editing.id}`, payload, getAuthHeaders());
      }
      setEditing(null);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this task type?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types/${selectedCT}/task-types/${id}`, getAuthHeaders());
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <div>
      {!propCaseTypeId ? (
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
          <label style={{ fontSize: '13px', fontWeight: 500 }}>Case Type:</label>
          <select value={selectedCT} onChange={e => setSelectedCT(e.target.value)} style={{ ...inputStyle, maxWidth: '280px' }}>
            <option value="">Select a case type...</option>
            {caseTypes.map(ct => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
          </select>
          {selectedCT && <button onClick={() => startEdit(null)} style={btnPrimary}>+ New Task Type</button>}
        </div>
      ) : (
        <div style={{ marginBottom: '16px' }}>
          <button onClick={() => startEdit(null)} style={btnPrimary}>+ New Task Type</button>
        </div>
      )}

      {!selectedCT ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)', fontSize: '13px' }}>Select a case type to manage its task types.</div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>Loading...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {taskTypes.length === 0 && <div style={{ color: 'var(--text-secondary)', fontSize: '13px', padding: '20px 0' }}>No task types defined. Create one to enable workflow-driven tasks.</div>}
          {taskTypes.map(tt => (
            <div key={tt.id} style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 500 }}>{tt.icon !== 'checkbox' ? tt.icon + ' ' : ''}{tt.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ background: tt.launch_type === 'required' ? '#22c55e20' : tt.launch_type === 'auto' ? '#a855f720' : '#3b82f620', color: tt.launch_type === 'required' ? '#22c55e' : tt.launch_type === 'auto' ? '#a855f7' : '#3b82f6', padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 600 }}>{tt.launch_type}</span>
                  {tt.launch_type === 'auto' && tt.trigger_event && <span style={{ background: 'var(--accent-color)20', color: 'var(--accent-color)', padding: '1px 6px', borderRadius: '8px', fontSize: '10px' }}>on: {tt.trigger_event.replace(/_/g, ' ')}</span>}
                  {tt.required_for_completion && <span style={{ background: '#f59e0b20', color: '#f59e0b', padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 600 }}>required for completion</span>}
                  {tt.workflow_id && <span>workflow linked</span>}
                  {tt.max_instances > 0 && <span>max: {tt.max_instances}</span>}
                  {tt.sla_minutes && <span>SLA: {tt.sla_minutes}m</span>}
                  {tt.estimated_duration_minutes && <span>~{tt.estimated_duration_minutes}m</span>}
                  {tt.launch_type !== 'discretionary' && tt.preconditions_json?.conditions?.length > 0 && <span>preconditions</span>}
                  {!tt.is_active && <span style={{ color: '#ef4444' }}>inactive</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button onClick={() => startEdit(tt)} style={{ ...btnSecondary, padding: '4px 8px', fontSize: '11px' }}>Edit</button>
                <button onClick={() => handleDelete(tt.id)} style={{ background: '#ef444420', color: '#ef4444', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit/Create dialog */}
      {editing && (() => {
        const schemaFields = caseTypeSchema?.fields || [];
        const standardFields = [
          { key: 'status', label: 'Status', type: 'text' },
          { key: 'priority', label: 'Priority', type: 'text' },
          { key: 'assignee_id', label: 'Assignee ID', type: 'number' },
          { key: 'team_id', label: 'Team ID', type: 'text' },
          { key: 'source', label: 'Source', type: 'text' },
        ];
        const allFields = [...standardFields, ...schemaFields.map(f => ({ key: `custom.${f.key}`, label: `${f.label} (custom)`, type: f.type }))];
        const operators = [
          { value: 'eq', label: 'equals' },
          { value: 'neq', label: 'not equals' },
          { value: 'in', label: 'is one of' },
          { value: 'not_in', label: 'is not one of' },
          { value: 'gt', label: '>' },
          { value: 'gte', label: '>=' },
          { value: 'lt', label: '<' },
          { value: 'lte', label: '<=' },
          { value: 'contains', label: 'contains' },
          { value: 'is_empty', label: 'is empty' },
          { value: 'is_not_empty', label: 'is not empty' },
        ];
        const preconditions = form.preconditions_json || { conditions: [], match: 'all' };
        const conditions = preconditions.conditions || [];
        const updateConditions = (newConditions) => setForm(f => ({ ...f, preconditions_json: { ...preconditions, conditions: newConditions } }));

        const sectionLabel = { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', marginTop: '12px', marginBottom: '4px', letterSpacing: '0.5px' };

        return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000 }}>
          <div style={{ background: 'var(--bg-primary)', borderRadius: '12px', padding: '24px', maxWidth: '600px', width: '90%', maxHeight: '85vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px' }}>{editing === 'new' ? 'New Task Type' : 'Edit Task Type'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

              {/* ── General ── */}
              <div style={sectionLabel}>General</div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500 }}>Name *</label>
                <input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} placeholder="e.g. Initial Assessment" />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500 }}>Description</label>
                <textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ ...inputStyle, minHeight: '50px' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500 }}>Instructions</label>
                <textarea value={form.instructions || ''} onChange={e => setForm(f => ({ ...f, instructions: e.target.value }))} style={{ ...inputStyle, minHeight: '50px' }} placeholder="Guidance shown to workers when they start this task" />
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: 500 }}>Launch Type</label>
                  <select value={form.launch_type || 'required'} onChange={e => setForm(f => ({ ...f, launch_type: e.target.value, ...( e.target.value !== 'auto' ? { trigger_event: '', trigger_event_config_json: {} } : {}) }))} style={inputStyle}>
                    <option value="required">Required (created with case)</option>
                    <option value="discretionary">Discretionary (added manually)</option>
                    <option value="auto">Auto (triggered by case events)</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: 500 }}>Default Priority</label>
                  <select value={form.default_priority || 'medium'} onChange={e => setForm(f => ({ ...f, default_priority: e.target.value }))} style={inputStyle}>
                    {['low', 'medium', 'high', 'critical'].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '12px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.required_for_completion || false} onChange={e => setForm(f => ({ ...f, required_for_completion: e.target.checked }))} />
                  Required for case completion
                </label>
              </div>

              {/* ── Trigger Event (auto tasks only) ── */}
              {form.launch_type === 'auto' && (<>
                <div style={sectionLabel}>Trigger Event</div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500 }}>Event *</label>
                  <select value={form.trigger_event || ''} onChange={e => setForm(f => ({ ...f, trigger_event: e.target.value, trigger_event_config_json: {} }))} style={inputStyle}>
                    <option value="">Select event...</option>
                    <option value="case_created">Case Created</option>
                    <option value="status_changed">Status Changed</option>
                    <option value="assigned">Case Assigned</option>
                    <option value="escalated">Case Escalated</option>
                    <option value="sla_warning">SLA Warning</option>
                    <option value="sla_breach">SLA Breach</option>
                    <option value="comment_added">Comment Added</option>
                    <option value="document_uploaded">Document Uploaded</option>
                    <option value="field_changed">Field Changed</option>
                    <option value="stage_entered">Stage Entered</option>
                    <option value="stage_completed">Stage Completed</option>
                  </select>
                </div>
                {form.trigger_event === 'status_changed' && (
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '12px', fontWeight: 500 }}>From Status (optional)</label>
                      <input value={form.trigger_event_config_json?.from_status || ''} onChange={e => setForm(f => ({ ...f, trigger_event_config_json: { ...f.trigger_event_config_json, from_status: e.target.value || undefined } }))} style={inputStyle} placeholder="e.g. open" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '12px', fontWeight: 500 }}>To Status (optional)</label>
                      <input value={form.trigger_event_config_json?.to_status || ''} onChange={e => setForm(f => ({ ...f, trigger_event_config_json: { ...f.trigger_event_config_json, to_status: e.target.value || undefined } }))} style={inputStyle} placeholder="e.g. in_progress" />
                    </div>
                  </div>
                )}
                {form.trigger_event === 'field_changed' && (
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 500 }}>Field Name (optional)</label>
                    <input value={form.trigger_event_config_json?.field_name || ''} onChange={e => setForm(f => ({ ...f, trigger_event_config_json: { ...f.trigger_event_config_json, field_name: e.target.value || undefined } }))} style={inputStyle} placeholder="e.g. priority" />
                  </div>
                )}
                {(form.trigger_event === 'stage_entered' || form.trigger_event === 'stage_completed') && (
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 500 }}>Stage Name (optional)</label>
                    <input value={form.trigger_event_config_json?.stage_name || ''} onChange={e => setForm(f => ({ ...f, trigger_event_config_json: { ...f.trigger_event_config_json, stage_name: e.target.value || undefined } }))} style={inputStyle} placeholder="e.g. Investigation" />
                  </div>
                )}
                {(form.trigger_event === 'sla_warning' || form.trigger_event === 'sla_breach') && (
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 500 }}>SLA Type (optional)</label>
                    <select value={form.trigger_event_config_json?.sla_type || ''} onChange={e => setForm(f => ({ ...f, trigger_event_config_json: { ...f.trigger_event_config_json, sla_type: e.target.value || undefined } }))} style={inputStyle}>
                      <option value="">Any</option>
                      <option value="response">Response</option>
                      <option value="resolution">Resolution</option>
                    </select>
                  </div>
                )}
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500 }}>Max Instances per Case</label>
                  <input type="number" min="0" value={form.max_instances ?? 1} onChange={e => setForm(f => ({ ...f, max_instances: parseInt(e.target.value) || 0 }))} style={inputStyle} />
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    0 = unlimited (repeatable). 1 = launch only once per case.
                  </div>
                </div>
              </>)}

              {/* ── Workflow & Stage ── */}
              <div style={sectionLabel}>Workflow & Stage</div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500 }}>Linked Workflow</label>
                <select value={form.workflow_id || ''} onChange={e => setForm(f => ({ ...f, workflow_id: e.target.value || null }))} style={inputStyle}>
                  <option value="">None (flat task)</option>
                  {workflows.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              {form.launch_type === 'discretionary' && (
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500 }}>Launch Form (optional)</label>
                  <FormSearchDropdown
                    value={form.launch_form_id || ''}
                    onChange={(id) => setForm(f => ({ ...f, launch_form_id: id || null }))}
                    placeholder="No form — auto-render task fields"
                    workspaceId={workspaceId}
                  />
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Shown to the worker before launch. If left empty and this task type has custom fields, those fields render as a single-column form.
                  </div>
                </div>
              )}
              {ctStages.length > 0 && (
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500 }}>Stage</label>
                  <select value={form.stage_id || ''} onChange={e => setForm(f => ({ ...f, stage_id: e.target.value || null }))} style={inputStyle}>
                    <option value="">None (not in any stage)</option>
                    {ctStages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}

              {/* ── Assignment ── */}
              <div style={sectionLabel}>Assignment</div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: 500 }}>Strategy</label>
                  <select value={form.default_assignment || 'case_assignee'} onChange={e => setForm(f => ({ ...f, default_assignment: e.target.value }))} style={inputStyle}>
                    <option value="case_assignee">Case Assignee</option>
                    <option value="team">Team</option>
                    <option value="round_robin">Round Robin</option>
                    <option value="specific_user">Specific User</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: 500 }}>Est. Duration (min)</label>
                  <input type="number" value={form.estimated_duration_minutes || ''} onChange={e => setForm(f => ({ ...f, estimated_duration_minutes: e.target.value }))} style={inputStyle} placeholder="e.g. 30" />
                </div>
              </div>

              {/* ── SLA & Deadlines ── */}
              <div style={sectionLabel}>SLA & Deadlines</div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: 500 }}>SLA (minutes)</label>
                  <input type="number" value={form.sla_minutes || ''} onChange={e => setForm(f => ({ ...f, sla_minutes: e.target.value }))} style={inputStyle} placeholder="e.g. 120" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: 500 }}>On SLA Breach</label>
                  <select value={form.deadline_action || ''} onChange={e => setForm(f => ({ ...f, deadline_action: e.target.value }))} style={inputStyle}>
                    <option value="">None (flag only)</option>
                    <option value="escalate">Escalate case</option>
                    <option value="reassign">Reassign task</option>
                    <option value="notify">Notify supervisor</option>
                  </select>
                </div>
              </div>

              {/* ── Task Fields (per-task custom schema) ── */}
              <div style={sectionLabel}>Task Fields</div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                Fields captured on each instance of this task. Accessible in columns as <code>task.custom_fields.&lt;key&gt;</code> and mappable from form fields via <code>task_field_key</code>.
              </div>
              {(() => {
                const schema = form.schema_json && typeof form.schema_json === 'object' ? form.schema_json : { fields: [] };
                const fields = Array.isArray(schema.fields) ? schema.fields : [];
                const setFields = (next) => setForm(f => ({ ...f, schema_json: { ...schema, fields: next } }));
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {fields.map((fld, idx) => (
                      <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px 70px 28px', gap: '6px', alignItems: 'center' }}>
                        <input value={fld.key || ''}
                          onChange={e => { const next = [...fields]; next[idx] = { ...fld, key: e.target.value.replace(/\s+/g, '_') }; setFields(next); }}
                          placeholder="key (e.g. severity)"
                          style={{ ...inputStyle, fontSize: '12px', padding: '4px 8px' }} />
                        <input value={fld.label || ''}
                          onChange={e => { const next = [...fields]; next[idx] = { ...fld, label: e.target.value }; setFields(next); }}
                          placeholder="Label"
                          style={{ ...inputStyle, fontSize: '12px', padding: '4px 8px' }} />
                        <select value={fld.type || 'text'}
                          onChange={e => { const next = [...fields]; next[idx] = { ...fld, type: e.target.value }; setFields(next); }}
                          style={{ ...inputStyle, fontSize: '12px', padding: '4px 8px' }}>
                          {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                          <input type="checkbox" checked={!!fld.required}
                            onChange={e => { const next = [...fields]; next[idx] = { ...fld, required: e.target.checked }; setFields(next); }} /> req
                        </label>
                        <button onClick={() => setFields(fields.filter((_, i) => i !== idx))}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px', padding: 0 }}>x</button>
                      </div>
                    ))}
                    <button onClick={() => setFields([...fields, { key: '', label: '', type: 'text', required: false }])}
                      style={{ ...btnSecondary, fontSize: '11px', padding: '4px 10px', alignSelf: 'flex-start' }}>+ Add Task Field</button>
                  </div>
                );
              })()}

              {/* ── Preconditions (not applicable to discretionary tasks — they're launched manually) ── */}
              {form.launch_type !== 'discretionary' && (<>
                <div style={sectionLabel}>Preconditions</div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  Task is only created/available when {' '}
                  <select value={preconditions.match || 'all'} onChange={e => setForm(f => ({ ...f, preconditions_json: { ...preconditions, match: e.target.value } }))}
                    style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '4px', color: 'var(--text-primary)', fontSize: '11px', padding: '1px 4px' }}>
                    <option value="all">all</option>
                    <option value="any">any</option>
                  </select>
                  {' '}of these conditions are met:
                </div>
                {conditions.map((cond, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <select value={cond.field || ''} onChange={e => { const c = [...conditions]; c[idx] = { ...c[idx], field: e.target.value }; updateConditions(c); }}
                      style={{ ...inputStyle, flex: 2, fontSize: '12px' }}>
                      <option value="">Field...</option>
                      {allFields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </select>
                    <select value={cond.op || 'eq'} onChange={e => { const c = [...conditions]; c[idx] = { ...c[idx], op: e.target.value }; updateConditions(c); }}
                      style={{ ...inputStyle, flex: 1, fontSize: '12px' }}>
                      {operators.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {!['is_empty', 'is_not_empty'].includes(cond.op) && (
                      <input value={cond.op === 'in' || cond.op === 'not_in' ? (Array.isArray(cond.value) ? cond.value.join(', ') : cond.value || '') : (cond.value ?? '')}
                        onChange={e => {
                          const c = [...conditions];
                          const val = (cond.op === 'in' || cond.op === 'not_in') ? e.target.value.split(',').map(s => s.trim()).filter(Boolean) : e.target.value;
                          c[idx] = { ...c[idx], value: val };
                          updateConditions(c);
                        }}
                        style={{ ...inputStyle, flex: 2, fontSize: '12px' }}
                        placeholder={cond.op === 'in' || cond.op === 'not_in' ? 'val1, val2, ...' : 'value'} />
                    )}
                    <button onClick={() => updateConditions(conditions.filter((_, i) => i !== idx))}
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px', padding: '0 4px', lineHeight: 1 }}>x</button>
                  </div>
                ))}
                <button onClick={() => updateConditions([...conditions, { field: '', op: 'eq', value: '' }])}
                  style={{ ...btnSecondary, fontSize: '11px', padding: '4px 10px', alignSelf: 'flex-start' }}>+ Add Condition</button>
              </>)}
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={() => setEditing(null)} style={btnSecondary}>Cancel</button>
              <button onClick={handleSave} style={btnPrimary}>Save</button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// In-Baskets Admin (Phase 7)
// ═══════════════════════════════════════════════════════════════════════════
function InBasketsAdmin({ workspaceId, getAuthHeaders }) {
  const [baskets, setBaskets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  // Reference data for visual builders
  const [caseTypesRef, setCaseTypesRef] = useState([]);
  const [teamsRef, setTeamsRef] = useState([]);
  const [membersRef, setMembersRef] = useState([]);
  const [rolesRef, setRolesRef] = useState([]);
  const [customFieldKeys, setCustomFieldKeys] = useState([]);
  const [taskFieldKeys, setTaskFieldKeys] = useState([]);
  // Column add popover
  const [showAddCol, setShowAddCol] = useState(false);
  const [customColField, setCustomColField] = useState('');

  const STATUSES = [
    { key: 'available', label: 'Available', color: '#22c55e' },
    { key: 'claimed', label: 'Claimed', color: '#3b82f6' },
    { key: 'completed', label: 'Completed', color: '#6b7280' },
    { key: 'skipped', label: 'Skipped', color: '#f59e0b' },
    { key: 'expired', label: 'Expired', color: '#ef4444' },
  ];
  const PRIORITIES = [
    { key: 'critical', label: 'Critical', color: '#ef4444' },
    { key: 'high', label: 'High', color: '#f97316' },
    { key: 'medium', label: 'Medium', color: '#eab308' },
    { key: 'low', label: 'Low', color: '#6b7280' },
  ];
  const SLA_STATUSES = [
    { key: 'on_track', label: 'On Track', color: '#22c55e' },
    { key: 'warning', label: 'Warning', color: '#f59e0b' },
    { key: 'breached', label: 'Breached', color: '#ef4444' },
  ];
  const AVAILABLE_COLUMNS = [
    { key: 'case_number', label: 'Case #', width: 120 },
    { key: 'step_name', label: 'Step', width: 180 },
    { key: 'priority', label: 'Priority', width: 80 },
    { key: 'sla_deadline', label: 'SLA Deadline', width: 140, format: 'countdown' },
    { key: 'case.title', label: 'Case Title', width: 250 },
    { key: 'case.case_type_name', label: 'Case Type', width: 140 },
    { key: 'task_type_name', label: 'Task Type', width: 140 },
    { key: 'claimed_by_name', label: 'Claimed By', width: 140 },
    { key: 'created_at', label: 'Received', width: 140, format: 'relative_time' },
    { key: 'status', label: 'Status', width: 100 },
  ];
  const DEFAULT_COLUMNS = [
    { key: 'case_number', label: 'Case #', width: 120 },
    { key: 'step_name', label: 'Step', width: 180 },
    { key: 'priority', label: 'Priority', width: 80 },
    { key: 'sla_deadline', label: 'SLA Deadline', width: 140, format: 'countdown' },
    { key: 'case.title', label: 'Case Title', width: 250 },
  ];

  const chipStyle = (active, color) => ({
    display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '14px', fontSize: '12px', cursor: 'pointer', fontWeight: 500, transition: 'all 0.15s',
    background: active ? `${color}20` : 'var(--bg-tertiary)', color: active ? color : 'var(--text-secondary)',
    border: active ? `1px solid ${color}40` : '1px solid var(--border-color)',
  });
  const sectionHeading = { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', letterSpacing: '0.5px', marginBottom: '6px' };

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/in-baskets`, getAuthHeaders());
      setBaskets(res.data || []);
    } catch { setBaskets([]); }
    setLoading(false);
  }, [workspaceId]);

  // Load reference data for visual pickers
  const loadRef = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const [ctRes, tRes, wRes, rRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types`, getAuthHeaders()),
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/teams`, getAuthHeaders()),
        axios.get(`${API_BASE_URL}/workspaces/${workspaceId}`, getAuthHeaders()),
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-roles`, getAuthHeaders()).catch(() => ({ data: [] })),
      ]);
      setCaseTypesRef(ctRes.data || []);
      setTeamsRef(tRes.data || []);
      setMembersRef(wRes.data?.members || []);
      setRolesRef((rRes.data || []).filter(r => r.is_active !== false));
      // Collect case custom field keys from all case types
      const cfKeys = new Set();
      (ctRes.data || []).forEach(ct => {
        (ct.schema_json?.fields || []).forEach(f => cfKeys.add(f.key || f.name));
      });
      setCustomFieldKeys([...cfKeys].sort());
      // Collect task custom field keys across all case types' task types
      try {
        const ttResults = await Promise.all(
          (ctRes.data || []).map(ct =>
            axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types/${ct.id}/task-types`, getAuthHeaders())
              .then(r => r.data || []).catch(() => [])
          )
        );
        const tfKeys = new Set();
        ttResults.forEach(tts => tts.forEach(tt => (tt.schema_json?.fields || []).forEach(f => tfKeys.add(f.key || f.name))));
        setTaskFieldKeys([...tfKeys].sort());
      } catch { setTaskFieldKeys([]); }
    } catch {}
  }, [workspaceId]);

  useEffect(() => { load(); loadRef(); }, [load, loadRef]);

  const parseObj = (val, fallback) => {
    if (!val) return fallback;
    if (typeof val === 'object') return val;
    try { return JSON.parse(val); } catch { return fallback; }
  };

  const startEdit = (ib) => {
    setEditing(ib || 'new');
    setShowAddCol(false);
    setCustomColField('');
    if (ib) {
      const filt = parseObj(ib.filter_json, {});
      const cols = parseObj(ib.columns_json, []);
      const acc = parseObj(ib.access_json, {});
      const dsort = parseObj(ib.default_sort_json, {});
      setForm({
        name: ib.name, description: ib.description || '', icon: ib.icon || 'inbox',
        basket_type: ib.basket_type || 'shared',
        filter: { statuses: filt.statuses || [], priorities: filt.priorities || [], case_type_ids: filt.case_type_ids || [], teams: filt.teams || [], roles: filt.roles || [], step_names: filt.step_names || [], sla_status: filt.sla_status || [], include_assigned_to_me: !!filt.include_assigned_to_me, field_filters: filt.field_filters || [], ui_filters: filt.ui_filters || [] },
        columns: cols.length > 0 ? cols : [...DEFAULT_COLUMNS],
        default_sort: { field: dsort.field || '', direction: dsort.direction || 'asc' },
        access: { teams: acc.teams || [], users: acc.users || [], case_role_ids: acc.case_role_ids || [] },
      });
    } else {
      setForm({
        name: '', description: '', icon: 'inbox', basket_type: 'shared',
        filter: { statuses: ['available', 'claimed'], priorities: [], case_type_ids: [], teams: [], roles: [], step_names: [], sla_status: [], include_assigned_to_me: false, field_filters: [], ui_filters: [] },
        columns: [...DEFAULT_COLUMNS],
        default_sort: { field: 'sla_deadline', direction: 'asc' },
        access: { teams: [], users: [], case_role_ids: [] },
      });
    }
  };

  const toggleFilterArray = (key, val) => {
    setForm(f => {
      const arr = f.filter[key] || [];
      return { ...f, filter: { ...f.filter, [key]: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] } };
    });
  };

  const handleSave = async () => {
    if (!form.name?.trim()) { alert('Name is required'); return; }
    const isPersonal = form.basket_type === 'personal';
    if (!isPersonal && !(form.filter?.teams?.length)) {
      alert('Shared in-baskets must include at least one team. Switch to Personal or pick a team in Filters → Teams.');
      return;
    }
    // Build clean filter (omit empty arrays/false)
    const filt = {};
    if (form.filter.statuses?.length) filt.statuses = form.filter.statuses;
    if (form.filter.priorities?.length) filt.priorities = form.filter.priorities;
    if (form.filter.case_type_ids?.length) filt.case_type_ids = form.filter.case_type_ids;
    if (!isPersonal && form.filter.teams?.length) filt.teams = form.filter.teams;
    if (form.filter.roles?.length) filt.roles = form.filter.roles;
    if (form.filter.step_names?.length) filt.step_names = form.filter.step_names;
    if (form.filter.sla_status?.length) filt.sla_status = form.filter.sla_status;
    if (!isPersonal && form.filter.include_assigned_to_me) filt.include_assigned_to_me = true;
    const cleanFieldFilters = (form.filter.field_filters || []).filter(ff => ff.field && (ff.value || ff.op === 'exists' || ff.op === 'not_exists'));
    if (cleanFieldFilters.length) filt.field_filters = cleanFieldFilters;
    const cleanUiFilters = (form.filter.ui_filters || []).filter(uf => uf.field);
    if (cleanUiFilters.length) filt.ui_filters = cleanUiFilters;
    // Access — roles only.
    const acc = {};
    if (form.access.case_role_ids?.length) acc.case_role_ids = form.access.case_role_ids;
    try {
      const dsort = form.default_sort?.field ? { field: form.default_sort.field, direction: form.default_sort.direction || 'asc' } : null;
      const payload = {
        name: form.name, description: form.description, icon: form.icon, basket_type: form.basket_type,
        filter_json: filt, columns_json: form.columns, access_json: acc,
        default_sort_json: dsort,
      };
      if (editing === 'new') {
        await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/in-baskets`, payload, getAuthHeaders());
      } else {
        await axios.patch(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/in-baskets/${editing.id}`, payload, getAuthHeaders());
      }
      setEditing(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this in-basket?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/in-baskets/${id}`, getAuthHeaders());
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  // Column helpers
  const addColumn = (col) => {
    setForm(f => ({ ...f, columns: [...f.columns, { ...col }] }));
    setShowAddCol(false);
    setCustomColField('');
  };
  const removeColumn = (idx) => setForm(f => ({ ...f, columns: f.columns.filter((_, i) => i !== idx) }));
  const moveColumn = (idx, dir) => {
    setForm(f => {
      const cols = [...f.columns];
      const target = idx + dir;
      if (target < 0 || target >= cols.length) return f;
      [cols[idx], cols[target]] = [cols[target], cols[idx]];
      return { ...f, columns: cols };
    });
  };
  const updateColumn = (idx, key, val) => {
    setForm(f => ({ ...f, columns: f.columns.map((c, i) => i === idx ? { ...c, [key]: val } : c) }));
  };

  // Filter chip component for multi-select
  const selectedColumnsKeys = (form.columns || []).map(c => c.key);
  const availableCols = AVAILABLE_COLUMNS.filter(c => !selectedColumnsKeys.includes(c.key));
  const availableCustomCols = customFieldKeys.filter(k => !selectedColumnsKeys.includes(`case.custom_fields.${k}`));
  const availableTaskCols = taskFieldKeys.filter(k => !selectedColumnsKeys.includes(`task.custom_fields.${k}`));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '15px' }}>In-Baskets</h3>
        <button onClick={() => startEdit(null)} style={btnPrimary}>+ New In-Basket</button>
      </div>
      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
        In-baskets are virtual queue views over work items. Each basket defines its own filters, column layout, and access control.
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>Loading...</div>
      ) : baskets.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)', fontSize: '13px' }}>No in-baskets configured. "My Work" is always available as the default inbox.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {baskets.map(ib => (
            <div key={ib.id} style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 500 }}>{ib.icon && ib.icon !== 'inbox' ? ib.icon + ' ' : ''}{ib.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px', display: 'flex', gap: '8px' }}>
                  <span style={{ background: ib.basket_type === 'shared' ? '#3b82f620' : '#8b5cf620', color: ib.basket_type === 'shared' ? '#3b82f6' : '#8b5cf6', padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 600 }}>{ib.basket_type}</span>
                  <span>{ib.item_count ?? 0} items</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button onClick={() => startEdit(ib)} style={{ ...btnSecondary, padding: '4px 8px', fontSize: '11px' }}>Edit</button>
                <button onClick={() => handleDelete(ib.id)} style={{ background: '#ef444420', color: '#ef4444', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Edit / Create Dialog ── */}
      {editing && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000 }}>
          <div style={{ background: 'var(--bg-primary)', borderRadius: '12px', padding: '24px', maxWidth: '680px', width: '95%', maxHeight: '90vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '16px' }}>{editing === 'new' ? 'New In-Basket' : 'Edit In-Basket'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* ── Name & Type ── */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 2 }}>
                  <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Name *</label>
                  <input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} placeholder="e.g. Claims Assessment Queue" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Type</label>
                  <select value={form.basket_type || 'shared'} onChange={e => setForm(f => {
                    const next = e.target.value;
                    // Switching to personal drops team-scoping + the implicit "assigned to me" toggle.
                    if (next === 'personal') {
                      return { ...f, basket_type: next, filter: { ...f.filter, teams: [], include_assigned_to_me: false } };
                    }
                    return { ...f, basket_type: next };
                  })} style={inputStyle}>
                    <option value="shared">Shared (Team WorkQueue)</option>
                    <option value="personal">Personal (My Inbox)</option>
                  </select>
                </div>
              </div>

              {/* ── Description ── */}
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Description</label>
                <input value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={inputStyle} placeholder="What this basket is for..." />
              </div>

              {/* ══════════ FILTERS ══════════ */}
              <div style={{ background: 'var(--bg-secondary)', borderRadius: '8px', padding: '14px', border: '1px solid var(--border-color)' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '14px' }}>Filters</div>

                {/* Statuses */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={sectionHeading}>Work Item Status</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {STATUSES.map(s => (
                      <span key={s.key} onClick={() => toggleFilterArray('statuses', s.key)} style={chipStyle(form.filter?.statuses?.includes(s.key), s.color)}>
                        {form.filter?.statuses?.includes(s.key) && <span>&#10003;</span>} {s.label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Priorities */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={sectionHeading}>Priority</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {PRIORITIES.map(p => (
                      <span key={p.key} onClick={() => toggleFilterArray('priorities', p.key)} style={chipStyle(form.filter?.priorities?.includes(p.key), p.color)}>
                        {form.filter?.priorities?.includes(p.key) && <span>&#10003;</span>} {p.label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* SLA Status */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={sectionHeading}>SLA Status</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {SLA_STATUSES.map(s => (
                      <span key={s.key} onClick={() => toggleFilterArray('sla_status', s.key)} style={chipStyle(form.filter?.sla_status?.includes(s.key), s.color)}>
                        {form.filter?.sla_status?.includes(s.key) && <span>&#10003;</span>} {s.label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Case Types */}
                {caseTypesRef.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={sectionHeading}>Case Types</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {caseTypesRef.map(ct => (
                        <span key={ct.id} onClick={() => toggleFilterArray('case_type_ids', ct.id)} style={chipStyle(form.filter?.case_type_ids?.includes(ct.id), '#8b5cf6')}>
                          {form.filter?.case_type_ids?.includes(ct.id) && <span>&#10003;</span>} {ct.icon || ''} {ct.name}
                        </span>
                      ))}
                    </div>
                    {!form.filter?.case_type_ids?.length && <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '4px' }}>None selected = all case types</div>}
                  </div>
                )}

                {/* Teams — only on shared baskets (Team WorkQueue analogue). Required: ≥1. */}
                {form.basket_type !== 'personal' && teamsRef.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={sectionHeading}>
                      Teams <span style={{ color: '#ef4444', fontWeight: 700 }}>*</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {teamsRef.map(t => (
                        <span key={t.id} onClick={() => toggleFilterArray('teams', t.id)} style={chipStyle(form.filter?.teams?.includes(t.id), '#06b6d4')}>
                          {form.filter?.teams?.includes(t.id) && <span>&#10003;</span>} {t.name}
                        </span>
                      ))}
                    </div>
                    <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                      Shared baskets must include at least one team — items shown are scoped to those teams' work.
                    </div>
                  </div>
                )}

                {/* Step Names */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={sectionHeading}>Step Names</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                    {(form.filter?.step_names || []).map((sn, i) => (
                      <span key={i} style={{ ...chipStyle(true, '#8b5cf6'), paddingRight: '4px' }}>
                        {sn}
                        <span onClick={() => setForm(f => ({ ...f, filter: { ...f.filter, step_names: f.filter.step_names.filter((_, j) => j !== i) } }))} style={{ marginLeft: '4px', cursor: 'pointer', opacity: 0.7, fontWeight: 700 }}>&times;</span>
                      </span>
                    ))}
                    <input placeholder="Add step name..." style={{ ...inputStyle, width: '140px', fontSize: '12px', padding: '4px 8px' }}
                      onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { const v = e.target.value.trim(); setForm(f => ({ ...f, filter: { ...f.filter, step_names: [...(f.filter.step_names || []), v] } })); e.target.value = ''; } }} />
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '4px' }}>Press Enter to add. Leave empty for all steps.</div>
                </div>

                {/* Fixed Field Filters (hidden from workers, always applied) */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={sectionHeading}>Fixed Field Filters</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>Always applied server-side. Workers cannot see or change these.</div>
                  {(form.filter?.field_filters || []).map((ff, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '4px' }}>
                      {customFieldKeys.length > 0 ? (
                        <select value={ff.field || ''} onChange={e => setForm(f => { const ffs = [...(f.filter.field_filters || [])]; ffs[idx] = { ...ffs[idx], field: e.target.value }; return { ...f, filter: { ...f.filter, field_filters: ffs } }; })} style={{ ...inputStyle, flex: 1, padding: '4px 6px', fontSize: '12px' }}>
                          <option value="">Select field...</option>
                          {customFieldKeys.map(k => <option key={k} value={k}>{k}</option>)}
                        </select>
                      ) : (
                        <input value={ff.field || ''} onChange={e => setForm(f => { const ffs = [...(f.filter.field_filters || [])]; ffs[idx] = { ...ffs[idx], field: e.target.value }; return { ...f, filter: { ...f.filter, field_filters: ffs } }; })} placeholder="field_key" style={{ ...inputStyle, flex: 1, padding: '4px 6px', fontSize: '12px' }} />
                      )}
                      <select value={ff.op || 'eq'} onChange={e => setForm(f => { const ffs = [...(f.filter.field_filters || [])]; ffs[idx] = { ...ffs[idx], op: e.target.value }; return { ...f, filter: { ...f.filter, field_filters: ffs } }; })} style={{ ...inputStyle, width: '90px', padding: '4px 6px', fontSize: '12px' }}>
                        <option value="eq">equals</option>
                        <option value="neq">not equal</option>
                        <option value="contains">contains</option>
                        <option value="gt">&gt;</option>
                        <option value="lt">&lt;</option>
                        <option value="exists">exists</option>
                        <option value="not_exists">not exists</option>
                      </select>
                      {ff.op !== 'exists' && ff.op !== 'not_exists' && (
                        <input value={ff.value || ''} onChange={e => setForm(f => { const ffs = [...(f.filter.field_filters || [])]; ffs[idx] = { ...ffs[idx], value: e.target.value }; return { ...f, filter: { ...f.filter, field_filters: ffs } }; })} placeholder="value" style={{ ...inputStyle, flex: 1, padding: '4px 6px', fontSize: '12px' }} />
                      )}
                      <button onClick={() => setForm(f => ({ ...f, filter: { ...f.filter, field_filters: f.filter.field_filters.filter((_, i) => i !== idx) } }))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px', padding: '0 4px', lineHeight: 1 }} title="Remove">&times;</button>
                    </div>
                  ))}
                  <button onClick={() => setForm(f => ({ ...f, filter: { ...f.filter, field_filters: [...(f.filter.field_filters || []), { field: '', op: 'eq', value: '' }] } }))} style={{ ...btnSecondary, padding: '3px 10px', fontSize: '11px', marginTop: '4px' }}>+ Add Fixed Filter</button>
                </div>

                {/* Worker UI Filters (shown in basket view for workers to pick values) */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={sectionHeading}>Worker UI Filters</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>Shown as dropdowns/inputs in the basket view. Workers select values at runtime.</div>
                  {(form.filter?.ui_filters || []).map((uf, idx) => (
                    <div key={idx} style={{ background: 'var(--bg-tertiary)', borderRadius: '6px', padding: '8px 10px', marginBottom: '6px', border: '1px solid var(--border-color)' }}>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
                        {customFieldKeys.length > 0 ? (
                          <select value={uf.field || ''} onChange={e => setForm(f => { const ufs = [...(f.filter.ui_filters || [])]; ufs[idx] = { ...ufs[idx], field: e.target.value }; return { ...f, filter: { ...f.filter, ui_filters: ufs } }; })} style={{ ...inputStyle, flex: 1, padding: '4px 6px', fontSize: '12px' }}>
                            <option value="">Select field...</option>
                            {customFieldKeys.map(k => <option key={k} value={k}>{k}</option>)}
                          </select>
                        ) : (
                          <input value={uf.field || ''} onChange={e => setForm(f => { const ufs = [...(f.filter.ui_filters || [])]; ufs[idx] = { ...ufs[idx], field: e.target.value }; return { ...f, filter: { ...f.filter, ui_filters: ufs } }; })} placeholder="field_key" style={{ ...inputStyle, flex: 1, padding: '4px 6px', fontSize: '12px' }} />
                        )}
                        <input value={uf.label || ''} onChange={e => setForm(f => { const ufs = [...(f.filter.ui_filters || [])]; ufs[idx] = { ...ufs[idx], label: e.target.value }; return { ...f, filter: { ...f.filter, ui_filters: ufs } }; })} placeholder="Display label" style={{ ...inputStyle, flex: 1, padding: '4px 6px', fontSize: '12px' }} />
                        <select value={uf.op || 'eq'} onChange={e => setForm(f => { const ufs = [...(f.filter.ui_filters || [])]; ufs[idx] = { ...ufs[idx], op: e.target.value }; return { ...f, filter: { ...f.filter, ui_filters: ufs } }; })} style={{ ...inputStyle, width: '90px', padding: '4px 6px', fontSize: '12px' }}>
                          <option value="eq">equals</option>
                          <option value="neq">not equal</option>
                          <option value="contains">contains</option>
                          <option value="gt">&gt;</option>
                          <option value="lt">&lt;</option>
                        </select>
                        <button onClick={() => setForm(f => ({ ...f, filter: { ...f.filter, ui_filters: f.filter.ui_filters.filter((_, i) => i !== idx) } }))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px', padding: '0 4px', lineHeight: 1 }} title="Remove">&times;</button>
                      </div>
                      <div>
                        <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '3px' }}>Dropdown options (one per line, leave empty for free text input)</div>
                        <textarea
                          value={(uf.options || []).join('\n')}
                          onChange={e => setForm(f => { const ufs = [...(f.filter.ui_filters || [])]; ufs[idx] = { ...ufs[idx], options: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) }; return { ...f, filter: { ...f.filter, ui_filters: ufs } }; })}
                          placeholder={'Bupa\nAetna\nCigna'}
                          style={{ ...inputStyle, fontSize: '11px', padding: '4px 6px', minHeight: '50px', resize: 'vertical', width: '100%', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                  ))}
                  <button onClick={() => setForm(f => ({ ...f, filter: { ...f.filter, ui_filters: [...(f.filter.ui_filters || []), { field: '', label: '', op: 'eq', options: [] }] } }))} style={{ ...btnSecondary, padding: '3px 10px', fontSize: '11px', marginTop: '4px' }}>+ Add UI Filter</button>
                </div>

                {/* Include Assigned to Me — implicit for personal baskets, only shown on shared */}
                {form.basket_type !== 'personal' ? (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px' }}>
                    <input type="checkbox" checked={!!form.filter?.include_assigned_to_me} onChange={e => setForm(f => ({ ...f, filter: { ...f.filter, include_assigned_to_me: e.target.checked } }))} />
                    <span>Include items assigned directly to me</span>
                  </label>
                ) : (
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                    Personal baskets always show only items assigned to (or claimed by) the viewing user.
                  </div>
                )}
              </div>

              {/* ══════════ COLUMNS ══════════ */}
              <div style={{ background: 'var(--bg-secondary)', borderRadius: '8px', padding: '14px', border: '1px solid var(--border-color)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>Columns</div>
                  <div style={{ position: 'relative' }}>
                    <button onClick={() => setShowAddCol(!showAddCol)} style={{ ...btnSecondary, padding: '3px 10px', fontSize: '11px' }}>+ Add Column</button>
                    {showAddCol && (
                      <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: '4px', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '8px', zIndex: 10, width: '240px', maxHeight: '260px', overflow: 'auto', boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
                        {availableCols.length > 0 && (
                          <>
                            <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '4px' }}>Standard</div>
                            {availableCols.map(c => (
                              <div key={c.key} onClick={() => addColumn(c)} style={{ padding: '5px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: 'var(--text-primary)' }}
                                onMouseEnter={e => e.target.style.background = 'var(--bg-tertiary)'} onMouseLeave={e => e.target.style.background = 'transparent'}>
                                {c.label} <span style={{ color: 'var(--text-tertiary)', fontSize: '10px' }}>({c.key})</span>
                              </div>
                            ))}
                          </>
                        )}
                        {availableCustomCols.length > 0 && (
                          <>
                            <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', marginTop: '8px', marginBottom: '4px' }}>Case Fields</div>
                            {availableCustomCols.map(k => (
                              <div key={k} onClick={() => addColumn({ key: `case.custom_fields.${k}`, label: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), width: 140 })} style={{ padding: '5px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: 'var(--text-primary)' }}
                                onMouseEnter={e => e.target.style.background = 'var(--bg-tertiary)'} onMouseLeave={e => e.target.style.background = 'transparent'}>
                                {k} <span style={{ color: 'var(--text-tertiary)', fontSize: '10px' }}>(case field)</span>
                              </div>
                            ))}
                          </>
                        )}
                        {availableTaskCols.length > 0 && (
                          <>
                            <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', marginTop: '8px', marginBottom: '4px' }}>Task Fields</div>
                            {availableTaskCols.map(k => (
                              <div key={k} onClick={() => addColumn({ key: `task.custom_fields.${k}`, label: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), width: 140 })} style={{ padding: '5px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: 'var(--text-primary)' }}
                                onMouseEnter={e => e.target.style.background = 'var(--bg-tertiary)'} onMouseLeave={e => e.target.style.background = 'transparent'}>
                                {k} <span style={{ color: 'var(--text-tertiary)', fontSize: '10px' }}>(task field)</span>
                              </div>
                            ))}
                          </>
                        )}
                        <div style={{ borderTop: '1px solid var(--border-color)', marginTop: '6px', paddingTop: '6px' }}>
                          <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '4px' }}>Custom Key</div>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <input value={customColField} onChange={e => setCustomColField(e.target.value)} placeholder="case.custom_fields.X or task.custom_fields.X" style={{ ...inputStyle, fontSize: '11px', padding: '3px 6px', flex: 1 }} />
                            <button onClick={() => { if (customColField.trim()) { addColumn({ key: customColField.trim(), label: customColField.trim().split('.').pop().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), width: 140 }); } }} style={{ ...btnPrimary, padding: '3px 8px', fontSize: '10px' }}>Add</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {(form.columns || []).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '16px', color: 'var(--text-tertiary)', fontSize: '12px' }}>No columns. Add columns above.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {(form.columns || []).map((col, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 8px', borderRadius: '6px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                        {/* Reorder buttons */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
                          <button onClick={() => moveColumn(idx, -1)} disabled={idx === 0} style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer', color: idx === 0 ? 'var(--text-tertiary)' : 'var(--text-secondary)', fontSize: '10px', padding: '0', lineHeight: 1 }} title="Move up">&#9650;</button>
                          <button onClick={() => moveColumn(idx, 1)} disabled={idx === form.columns.length - 1} style={{ background: 'none', border: 'none', cursor: idx === form.columns.length - 1 ? 'default' : 'pointer', color: idx === form.columns.length - 1 ? 'var(--text-tertiary)' : 'var(--text-secondary)', fontSize: '10px', padding: '0', lineHeight: 1 }} title="Move down">&#9660;</button>
                        </div>
                        {/* Label (editable) */}
                        <input value={col.label} onChange={e => updateColumn(idx, 'label', e.target.value)} style={{ ...inputStyle, flex: 1, padding: '3px 6px', fontSize: '12px', fontWeight: 500 }} title="Column label" />
                        {/* Key (read-only) */}
                        <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontFamily: 'monospace', minWidth: '80px' }}>{col.key}</span>
                        {/* Width */}
                        <input type="number" value={col.width || 120} onChange={e => updateColumn(idx, 'width', parseInt(e.target.value) || 120)} style={{ ...inputStyle, width: '56px', padding: '3px 4px', fontSize: '11px', textAlign: 'center' }} title="Width (px)" />
                        {/* Format */}
                        <select value={col.format || ''} onChange={e => updateColumn(idx, 'format', e.target.value || undefined)} style={{ ...inputStyle, width: '90px', padding: '3px 4px', fontSize: '11px' }} title="Display format">
                          <option value="">Default</option>
                          <option value="countdown">Countdown</option>
                          <option value="relative_time">Relative</option>
                          <option value="currency">Currency</option>
                          <option value="date">Date</option>
                        </select>
                        {/* Sortable */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer', fontSize: '10px', color: col.sortable ? 'var(--accent-color)' : 'var(--text-tertiary)', whiteSpace: 'nowrap' }} title="Allow sorting by this column">
                          <input type="checkbox" checked={!!col.sortable} onChange={e => updateColumn(idx, 'sortable', e.target.checked)} style={{ width: '12px', height: '12px' }} />
                          Sort
                        </label>
                        {/* Remove */}
                        <button onClick={() => removeColumn(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '14px', padding: '0 2px', lineHeight: 1 }} title="Remove column">&times;</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ══════════ DEFAULT SORT ══════════ */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Default Sort Column</label>
                  <select value={form.default_sort?.field || ''} onChange={e => setForm(f => ({ ...f, default_sort: { ...f.default_sort, field: e.target.value } }))} style={inputStyle}>
                    <option value="">None (SLA deadline)</option>
                    {(form.columns || []).filter(c => c.sortable).map(c => (
                      <option key={c.key} value={c.key}>{c.label || c.key}</option>
                    ))}
                    <option value="priority">Priority</option>
                    <option value="sla_deadline">SLA Deadline</option>
                    <option value="created_at">Created At</option>
                    <option value="step_name">Step Name</option>
                    <option value="status">Status</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Direction</label>
                  <select value={form.default_sort?.direction || 'asc'} onChange={e => setForm(f => ({ ...f, default_sort: { ...f.default_sort, direction: e.target.value } }))} style={inputStyle}>
                    <option value="asc">Ascending</option>
                    <option value="desc">Descending</option>
                  </select>
                </div>
              </div>

              {/* ══════════ ACCESS CONTROL (roles only) ══════════ */}
              <div style={{ background: 'var(--bg-secondary)', borderRadius: '8px', padding: '14px', border: '1px solid var(--border-color)' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px' }}>Access Control</div>
                {rolesRef.length > 0 ? (
                  <div>
                    <div style={sectionHeading}>Restrict to Roles</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {rolesRef.map(r => {
                        const sel = form.access?.case_role_ids?.includes(r.id);
                        const color = r.color || '#6366f1';
                        return (
                          <span key={r.id} onClick={() => {
                            setForm(f => {
                              const arr = f.access.case_role_ids || [];
                              return { ...f, access: { ...f.access, case_role_ids: sel ? arr.filter(x => x !== r.id) : [...arr, r.id] } };
                            });
                          }} style={chipStyle(sel, color)}>
                            {sel && <span>&#10003;</span>} {r.name}
                          </span>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '6px', fontStyle: 'italic' }}>
                      {form.access?.case_role_ids?.length
                        ? 'Workers with any of these active case roles will see this basket in the Case Client.'
                        : 'No roles selected — visible to all workspace members.'}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                    No case roles defined yet — basket will be visible to all workspace members. Define roles in the Roles tab to restrict access.
                  </div>
                )}
              </div>
            </div>

            {/* ── Footer ── */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => setEditing(null)} style={btnSecondary}>Cancel</button>
              <button onClick={handleSave} style={btnPrimary}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Stages Admin (Phase 8)
// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
// ROLES ADMIN
// ═══════════════════════════════════════════════════════════════════════════

function RolesAdmin({ workspaceId, getAuthHeaders }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | role obj
  const [form, setForm] = useState({ name: '', description: '', color: '#6366f1' });
  const [expandedRole, setExpandedRole] = useState(null);
  const [roleUsers, setRoleUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [addUserId, setAddUserId] = useState('');

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-roles`, getAuthHeaders());
      setRoles(res.data || []);
    } catch { setRoles([]); }
    setLoading(false);
  }, [workspaceId]);

  const loadMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/workspaces/${workspaceId}`, getAuthHeaders());
      setMembers(res.data?.members || []);
    } catch {}
  }, [workspaceId]);

  useEffect(() => { load(); loadMembers(); }, [load, loadMembers]);

  const loadRoleUsers = async (roleId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-roles/${roleId}/users`, getAuthHeaders());
      setRoleUsers(res.data || []);
    } catch { setRoleUsers([]); }
  };

  const handleSave = async () => {
    if (!form.name?.trim()) { alert('Name is required'); return; }
    try {
      if (editing === 'new') {
        await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-roles`, form, getAuthHeaders());
      } else {
        await axios.patch(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-roles/${editing.id}`, form, getAuthHeaders());
      }
      setEditing(null);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-roles/${id}`, getAuthHeaders());
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleAssignUser = async (roleId) => {
    if (!addUserId) return;
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-roles/${roleId}/users/${addUserId}`, {}, getAuthHeaders());
      setAddUserId('');
      loadRoleUsers(roleId);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to assign'); }
  };

  const handleRemoveUser = async (roleId, userId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-roles/${roleId}/users/${userId}`, getAuthHeaders());
      loadRoleUsers(roleId);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to remove'); }
  };

  const startEdit = (r) => {
    setEditing(r || 'new');
    setForm(r ? { name: r.name, description: r.description || '', color: r.color || '#6366f1' } : { name: '', description: '', color: '#6366f1' });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '15px' }}>Case Roles</h3>
        <button onClick={() => startEdit(null)} style={btnPrimary}>+ New Role</button>
      </div>
      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
        Roles define expertise areas (e.g. Assessor, Reviewer, Approver). Assign users to roles, then route work items to roles in task workflows.
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>Loading...</div> : roles.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)', fontSize: '13px' }}>No roles defined yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {roles.map(r => (
            <div key={r.id} style={{ ...cardStyle, overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: r.color || '#6366f1', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 500 }}>{r.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                      {r.user_count} user{r.user_count !== 1 ? 's' : ''}{r.description ? ` — ${r.description}` : ''}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button onClick={() => { setExpandedRole(expandedRole === r.id ? null : r.id); if (expandedRole !== r.id) loadRoleUsers(r.id); }} style={{ ...btnSecondary, padding: '4px 8px', fontSize: '11px' }}>{expandedRole === r.id ? 'Collapse' : 'Users'}</button>
                  <button onClick={() => startEdit(r)} style={{ ...btnSecondary, padding: '4px 8px', fontSize: '11px' }}>Edit</button>
                  <button onClick={() => handleDelete(r.id)} style={{ background: '#ef444420', color: '#ef4444', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px' }}>Delete</button>
                </div>
              </div>
              {expandedRole === r.id && (
                <div style={{ padding: '0 14px 12px', borderTop: '1px solid var(--border-color)' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-secondary)', margin: '10px 0 6px' }}>Assigned Users</div>
                  {roleUsers.length === 0 ? (
                    <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>No users assigned.</div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                      {roleUsers.map(u => (
                        <span key={u.user_id} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'var(--bg-tertiary)', padding: '3px 8px', borderRadius: '12px', fontSize: '12px' }}>
                          {u.username || `User ${u.user_id}`}
                          <span onClick={() => handleRemoveUser(r.id, u.user_id)} style={{ cursor: 'pointer', color: '#ef4444', fontWeight: 700, marginLeft: '2px' }}>&times;</span>
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <select value={addUserId} onChange={e => setAddUserId(e.target.value)} style={{ ...inputStyle, flex: 1, padding: '4px 8px', fontSize: '12px' }}>
                      <option value="">Select user to add...</option>
                      {members.filter(m => !roleUsers.some(ru => ru.user_id === m.user_id)).map(m => (
                        <option key={m.user_id} value={m.user_id}>{m.username || `User ${m.user_id}`}</option>
                      ))}
                    </select>
                    <button onClick={() => handleAssignUser(r.id)} disabled={!addUserId} style={{ ...btnPrimary, padding: '4px 12px', fontSize: '12px' }}>Add</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit/Create dialog */}
      {editing && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000 }}>
          <div style={{ background: 'var(--bg-primary)', borderRadius: '12px', padding: '24px', maxWidth: '420px', width: '95%' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px' }}>{editing === 'new' ? 'New Role' : 'Edit Role'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} placeholder="e.g. Assessor, Reviewer" />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Description</label>
                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={inputStyle} placeholder="What this role does" />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>Color</label>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} style={{ width: '36px', height: '30px', border: 'none', background: 'none', cursor: 'pointer' }} />
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{form.color}</span>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => setEditing(null)} style={btnSecondary}>Cancel</button>
              <button onClick={handleSave} style={btnPrimary}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Case Client config (worker UI per-workspace settings) ────────────────
function CaseClientConfigAdmin({ workspaceId, getAuthHeaders }) {
  const [config, setConfig] = useState(null);
  const [caseTypes, setCaseTypes] = useState([]);
  const [inBaskets, setInBaskets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError('');
    try {
      const [cfg, cts, ibs] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-client/config`, getAuthHeaders()).then(r => r.data),
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types`, getAuthHeaders()).then(r => r.data || []).catch(() => []),
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/in-baskets`, getAuthHeaders()).then(r => r.data || []).catch(() => []),
      ]);
      setConfig({
        enabled: !!cfg.enabled,
        add_case_enabled: cfg.add_case_enabled !== false,
        docuforge_enabled: !!cfg.docuforge_enabled,
        branding: cfg.branding || { name: '', logo_url: '', accent_color: '' },
        visible_case_type_ids: Array.isArray(cfg.visible_case_type_ids) ? cfg.visible_case_type_ids : [],
        default_inbasket_ids: Array.isArray(cfg.default_inbasket_ids) ? cfg.default_inbasket_ids : [],
      });
      setCaseTypes(cts);
      setInBaskets(ibs);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load xavvy Client config.');
    }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!config) return;
    setSaving(true);
    setError('');
    setSavedAt(null);
    try {
      const branding = config.branding || {};
      const cleanBranding = (branding.name || branding.logo_url || branding.accent_color)
        ? {
            name: branding.name || undefined,
            logo_url: branding.logo_url || undefined,
            accent_color: branding.accent_color || undefined,
          }
        : null;
      const body = {
        enabled: config.enabled,
        add_case_enabled: config.add_case_enabled,
        docuforge_enabled: config.docuforge_enabled,
        branding: cleanBranding,
        visible_case_type_ids: config.visible_case_type_ids,
        default_inbasket_ids: config.default_inbasket_ids,
      };
      await axios.put(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-client/config`, body, getAuthHeaders());
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed.');
    }
    setSaving(false);
  };

  const toggleId = (key, id) => {
    setConfig(c => {
      const arr = new Set(c[key]);
      if (arr.has(id)) arr.delete(id); else arr.add(id);
      return { ...c, [key]: Array.from(arr) };
    });
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Loading…</div>;
  if (!config) return <div style={{ padding: '20px', color: '#ef4444' }}>{error || 'Could not load.'}</div>;

  return (
    <div style={{ maxWidth: '780px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {error && <div style={{ background: '#ef444420', color: '#ef4444', padding: '10px 12px', borderRadius: '6px', fontSize: '13px' }}>{error}</div>}

      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 4px', fontSize: '15px' }}>Worker UI</h3>
        <p style={{ margin: '0 0 14px', fontSize: '12px', color: 'var(--text-secondary)' }}>
          The xavvy Client (<code>/case-client</code>) is the role-driven worker view of casematrix. When disabled, members can still preview the shell but the disabled banner is shown.
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', marginBottom: '8px' }}>
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={e => setConfig(c => ({ ...c, enabled: e.target.checked }))}
          />
          <span><strong>Enable xavvy Client</strong> for this workspace</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', marginBottom: '8px' }}>
          <input
            type="checkbox"
            checked={config.add_case_enabled}
            onChange={e => setConfig(c => ({ ...c, add_case_enabled: e.target.checked }))}
          />
          <span>Allow workers to <strong>create new cases</strong> from the Add Case wizard</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
          <input
            type="checkbox"
            checked={config.docuforge_enabled}
            onChange={e => setConfig(c => ({ ...c, docuforge_enabled: e.target.checked }))}
          />
          <span>Show the <strong>Docuforge</strong> section in the worker rail (browse documents, review extractions)</span>
        </label>
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 4px', fontSize: '15px' }}>Branding</h3>
        <p style={{ margin: '0 0 14px', fontSize: '12px', color: 'var(--text-secondary)' }}>
          Optional. Used in the Case Client header. Leave blank to use the default xavvy branding.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Display name</label>
            <input
              value={config.branding?.name || ''}
              onChange={e => setConfig(c => ({ ...c, branding: { ...(c.branding || {}), name: e.target.value } }))}
              style={inputStyle}
              placeholder="e.g. Acme Operations"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Accent color</label>
            <input
              type="color"
              value={config.branding?.accent_color || '#6366f1'}
              onChange={e => setConfig(c => ({ ...c, branding: { ...(c.branding || {}), accent_color: e.target.value } }))}
              style={{ ...inputStyle, height: '38px', padding: '2px 6px' }}
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Logo URL</label>
            <input
              value={config.branding?.logo_url || ''}
              onChange={e => setConfig(c => ({ ...c, branding: { ...(c.branding || {}), logo_url: e.target.value } }))}
              style={inputStyle}
              placeholder="https://…/logo.png"
            />
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 4px', fontSize: '15px' }}>Visible case types</h3>
        <p style={{ margin: '0 0 14px', fontSize: '12px', color: 'var(--text-secondary)' }}>
          Optional allowlist. When empty, all active case types are visible. When set, the worker work-list (with role scoping) only shows cases of these types.
        </p>
        {caseTypes.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>No case types in this workspace yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {caseTypes.map(ct => (
              <label key={ct.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', padding: '4px 0' }}>
                <input
                  type="checkbox"
                  checked={config.visible_case_type_ids.includes(ct.id)}
                  onChange={() => toggleId('visible_case_type_ids', ct.id)}
                />
                <span style={{ fontSize: '16px' }}>{ct.icon || '📋'}</span>
                <span>{ct.name}</span>
                {!ct.is_active && <span style={{ fontSize: '10px', color: '#ef4444' }}>inactive</span>}
              </label>
            ))}
          </div>
        )}
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 4px', fontSize: '15px' }}>Default in-baskets</h3>
        <p style={{ margin: '0 0 14px', fontSize: '12px', color: 'var(--text-secondary)' }}>
          Optional list of in-baskets pre-pinned in the worker shell. Leave empty to fall back to the in-baskets each worker has access to.
        </p>
        {inBaskets.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>No in-baskets configured. Create them in the In-Baskets tab.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {inBaskets.map(ib => (
              <label key={ib.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', padding: '4px 0' }}>
                <input
                  type="checkbox"
                  checked={config.default_inbasket_ids.includes(ib.id)}
                  onChange={() => toggleId('default_inbasket_ids', ib.id)}
                />
                <span>{ib.name}</span>
                {ib.basket_type === 'personal' && <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>personal</span>}
              </label>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button onClick={save} disabled={saving} style={btnPrimary}>
          {saving ? 'Saving…' : 'Save xavvy Client config'}
        </button>
        {savedAt && <span style={{ fontSize: '12px', color: '#10b981' }}>Saved at {savedAt}</span>}
      </div>
    </div>
  );
}


function StagesAdmin({ workspaceId, caseTypes, getAuthHeaders, caseTypeId: propCaseTypeId }) {
  const [selectedCT, setSelectedCT] = useState(propCaseTypeId || '');
  const [stages, setStages] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [ruleSets, setRuleSets] = useState([]);

  useEffect(() => {
    if (!workspaceId) return;
    axios.get(`${API_BASE_URL}/api/rules/workspaces/${workspaceId}/sets`, getAuthHeaders())
      .then(r => setRuleSets(r.data?.items || r.data || []))
      .catch(() => {});
  }, [workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (propCaseTypeId) setSelectedCT(propCaseTypeId); }, [propCaseTypeId]);

  const load = useCallback(async () => {
    if (!workspaceId || !selectedCT) { setStages([]); setTaskTypes([]); return; }
    setLoading(true);
    try {
      const [stgRes, ttRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types/${selectedCT}/stages`, getAuthHeaders()),
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types/${selectedCT}/task-types`, getAuthHeaders()),
      ]);
      setStages(stgRes.data || []);
      setTaskTypes(ttRes.data || []);
    } catch { setStages([]); setTaskTypes([]); }
    setLoading(false);
  }, [workspaceId, selectedCT]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (stg) => {
    setEditing(stg || 'new');
    setForm(stg ? { ...stg } : { name: '', description: '', entry_mode: 'auto', status_on_enter: '', sla_minutes: '', icon: 'step', color: '', advance_condition_rule_set_id: '' });
  };

  const handleSave = async () => {
    if (!form.name?.trim()) { alert('Name is required'); return; }
    try {
      const payload = { ...form, sla_minutes: form.sla_minutes ? parseInt(form.sla_minutes) : null };
      if (editing === 'new') {
        await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types/${selectedCT}/stages`, payload, getAuthHeaders());
      } else {
        await axios.patch(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types/${selectedCT}/stages/${editing.id}`, payload, getAuthHeaders());
      }
      setEditing(null);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this stage?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types/${selectedCT}/stages/${id}`, getAuthHeaders());
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleReorder = async (dragIdx, dropIdx) => {
    if (dragIdx === dropIdx) return;
    const reordered = [...stages];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    setStages(reordered);
    try {
      await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types/${selectedCT}/stages/reorder`, {
        stage_ids: reordered.map(s => s.id),
      }, getAuthHeaders());
    } catch { load(); }
  };

  // Get status options from case type's workflow
  const selectedCTObj = caseTypes.find(ct => ct.id === selectedCT);
  const statusOptions = selectedCTObj?.status_workflow_json?.statuses?.map(s => s.key) || ['open', 'in_progress', 'under_review', 'resolved', 'closed'];

  return (
    <div>
      {!propCaseTypeId ? (
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
          <label style={{ fontSize: '13px', fontWeight: 500 }}>Case Type:</label>
          <select value={selectedCT} onChange={e => setSelectedCT(e.target.value)} style={{ ...inputStyle, maxWidth: '280px' }}>
            <option value="">Select a case type...</option>
            {caseTypes.map(ct => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
          </select>
          {selectedCT && <button onClick={() => startEdit(null)} style={btnPrimary}>+ New Stage</button>}
        </div>
      ) : (
        <div style={{ marginBottom: '16px' }}>
          <button onClick={() => startEdit(null)} style={btnPrimary}>+ New Stage</button>
        </div>
      )}

      {!selectedCT ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)', fontSize: '13px' }}>Select a case type to manage its stages.</div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>Loading...</div>
      ) : stages.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)', fontSize: '13px' }}>
          No stages defined. Stages are optional — without them, cases use a flat task list.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* Visual stage pipeline */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '12px', overflowX: 'auto', padding: '4px 0' }}>
            {stages.map((stg, idx) => (
              <React.Fragment key={stg.id}>
                {idx > 0 && <div style={{ width: '20px', height: '2px', background: stg.color || 'var(--border-color)', flexShrink: 0 }} />}
                <div style={{
                  padding: '6px 14px', borderRadius: '16px', fontSize: '12px', fontWeight: 500,
                  border: `1.5px solid ${stg.color || 'var(--accent-color)'}`,
                  background: `${stg.color || 'var(--accent-color)'}15`,
                  color: stg.color || 'var(--accent-color)', whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                  {stg.order + 1}. {stg.name}
                  {stg.entry_mode === 'manual' && <span style={{ marginLeft: '4px', fontSize: '10px', opacity: 0.7 }}>(manual)</span>}
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* Stage list */}
          {stages.map((stg, idx) => {
            const stageTTs = taskTypes.filter(tt => tt.stage_id === stg.id);
            return (
              <div key={stg.id} style={{ ...cardStyle, cursor: 'default' }}
                draggable
                onDragStart={e => e.dataTransfer.setData('stg-idx', idx)}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); handleReorder(parseInt(e.dataTransfer.getData('stg-idx')), idx); }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', cursor: 'grab' }} title="Drag to reorder">::</span>
                    {stg.color && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: stg.color, flexShrink: 0 }} />}
                    <div>
                      <span style={{ fontWeight: 600, fontSize: '14px' }}>{stg.name}</span>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', gap: '8px', marginTop: '2px' }}>
                        <span>Order: {stg.order}</span>
                        <span>Entry: {stg.entry_mode}</span>
                        {stg.status_on_enter && <span>Sets status: {stg.status_on_enter}</span>}
                        {stg.sla_minutes && <span>SLA: {stg.sla_minutes}m</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    {stageTTs.length > 0 && (
                      <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginRight: '8px' }}>
                        {stageTTs.length} task type{stageTTs.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    <button onClick={() => startEdit(stg)} style={{ ...btnSecondary, padding: '4px 8px', fontSize: '11px' }}>Edit</button>
                    <button onClick={() => handleDelete(stg.id)} style={{ background: '#ef444420', color: '#ef4444', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px' }}>Delete</button>
                  </div>
                </div>
                {stg.description && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>{stg.description}</div>}
                {stageTTs.length > 0 && (
                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>Task types in this stage:</div>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {stageTTs.map(tt => (
                        <span key={tt.id} style={{ background: 'var(--bg-tertiary)', padding: '2px 8px', borderRadius: '8px', fontSize: '11px' }}>
                          {tt.name} ({tt.launch_type})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Edit dialog */}
      {editing && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000 }}>
          <div style={{ background: 'var(--bg-primary)', borderRadius: '12px', padding: '24px', maxWidth: '500px', width: '90%', maxHeight: '80vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px' }}>{editing === 'new' ? 'New Stage' : 'Edit Stage'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500 }}>Name *</label>
                <input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} placeholder="e.g. Intake, Investigation, Resolution" />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500 }}>Description</label>
                <textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ ...inputStyle, minHeight: '60px' }} placeholder="What happens in this stage?" />
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: 500 }}>Entry Mode</label>
                  <select value={form.entry_mode || 'auto'} onChange={e => setForm(f => ({ ...f, entry_mode: e.target.value }))} style={inputStyle}>
                    <option value="auto">Auto (advance when all required tasks done)</option>
                    <option value="manual">Manual (case worker clicks Advance)</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: 500 }}>Stage SLA (minutes)</label>
                  <input type="number" value={form.sla_minutes || ''} onChange={e => setForm(f => ({ ...f, sla_minutes: e.target.value }))} style={inputStyle} placeholder="e.g. 480" />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 500 }}>Auto-Set Case Status on Enter</label>
                <select value={form.status_on_enter || ''} onChange={e => setForm(f => ({ ...f, status_on_enter: e.target.value || null }))} style={inputStyle}>
                  <option value="">None (keep current status)</option>
                  {statusOptions.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>When a case enters this stage, auto-transition its status.</div>
              </div>
              {ruleSets.length > 0 && (
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 500 }}>Advance Condition Rule Set <span style={{ color: '#8b5cf6', fontWeight: 400, fontSize: '11px' }}>(Rules Engine)</span></label>
                  <select value={form.advance_condition_rule_set_id || ''} onChange={e => setForm(f => ({ ...f, advance_condition_rule_set_id: e.target.value || null }))} style={inputStyle}>
                    <option value="">None (always allow advance)</option>
                    {ruleSets.map(rs => <option key={rs.id} value={rs.id}>{rs.name}</option>)}
                  </select>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    Rule set is evaluated before auto- or manual advance. If it returns <code>outputs.continue = false</code>, the advance is blocked.
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: 500 }}>Color</label>
                  <input type="color" value={form.color || '#3b82f6'} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} style={{ ...inputStyle, height: '36px', padding: '2px' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: 500 }}>Icon</label>
                  <input value={form.icon || 'step'} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} style={inputStyle} placeholder="step" />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={() => setEditing(null)} style={btnSecondary}>Cancel</button>
              <button onClick={handleSave} style={btnPrimary}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
// Automation Rules Admin (within case type editor)
// ═══════════════════════════════════════════════════════════════════════════

// Main Admin Component (tabbed)
// ═══════════════════════════════════════════════════════════════════════════
const adminTabStyle = (active) => ({ padding: '8px 16px', cursor: 'pointer', borderBottom: active ? '2px solid var(--accent-color)' : '2px solid transparent', color: active ? 'var(--accent-color)' : 'var(--text-secondary)', fontWeight: active ? 600 : 400, fontSize: '13px', background: 'transparent', border: 'none', borderBottomWidth: '2px', borderBottomStyle: 'solid', borderBottomColor: active ? 'var(--accent-color)' : 'transparent' });

export default function CaseTypeAdmin({ onBack, workspaceId, currentUser = null }) {
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const [adminTab, setAdminTab] = useState('case_types');
  const [caseTypes, setCaseTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingType, setEditingType] = useState(null); // null | 'new' | CaseType object
  const [caseCounts, setCaseCounts] = useState({}); // { case_type_id: count }

  const load = useCallback(async () => {
    if (!workspaceId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types?active_only=false`, getAuthHeaders());
      setCaseTypes(res.data || []);
    } catch { setCaseTypes([]); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    if (!workspaceId) { alert('No workspace selected. Please select a workspace first.'); return; }
    try {
      if (editingType && editingType !== 'new') {
        await axios.patch(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types/${editingType.id}`, data, getAuthHeaders());
      } else {
        await axios.post(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types`, data, getAuthHeaders());
      }
      setEditingType(null);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save case type'); }
  };

  const handleDelete = async (id) => {
    if (!workspaceId) return;
    if (!confirm('This will deactivate the case type. Cases of this type won\'t be affected.')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types/${id}`, getAuthHeaders());
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete case type'); }
  };

  // Full-page case type editor (replaces entire admin view when editing)
  if (editingType) {
    return (
      <CaseTypeEditor
        caseType={editingType === 'new' ? null : editingType}
        onSave={handleSave}
        onBack={() => setEditingType(null)}
        workspaceId={workspaceId}
        caseTypes={caseTypes}
        getAuthHeaders={getAuthHeaders}
      />
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={onBack} style={{ ...btnSecondary, padding: '6px 12px' }}>← Back</button>
          <h2 style={{ margin: 0, fontSize: '16px' }}>casematrix Admin</h2>
        </div>
        {adminTab === 'case_types' && (
          <button onClick={() => setEditingType('new')} style={{ ...btnPrimary, opacity: workspaceId ? 1 : 0.5 }} disabled={!workspaceId}>+ New Case Type</button>
        )}
      </div>

      {/* Admin tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)', padding: '0 20px', background: 'var(--bg-secondary)' }}>
        {[
          { key: 'case_types', label: 'Case Types' },
          { key: 'in_baskets', label: 'In-Baskets' },
          { key: 'roles', label: 'Roles' },
          { key: 'teams', label: 'Teams' },
          { key: 'calendars', label: 'Business Calendars' },
          { key: 'rules', label: 'Decision Tables' },
          { key: 'notifications', label: 'Notifications' },
          { key: 'case_client', label: 'Case Client' },
          { key: 'layouts', label: 'Layouts' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setAdminTab(tab.key)} style={adminTabStyle(adminTab === tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>

      <div style={bodyStyle}>
        {/* Case Types tab */}
        {adminTab === 'case_types' && (
          <>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Loading...</div>
            ) : caseTypes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>📋</div>
                <div style={{ fontSize: '15px', marginBottom: '8px' }}>No case types yet</div>
                <div style={{ fontSize: '13px' }}>Create a case type to define the structure and lifecycle for your cases.</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
                {caseTypes.map(ct => (
                  <div key={ct.id} style={{ ...cardStyle, opacity: ct.is_active ? 1 : 0.5 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '22px' }}>{ct.icon}</span>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '14px' }}>{ct.name}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{ct.slug}</div>
                        </div>
                      </div>
                      {!ct.is_active && <span style={{ background: '#ef444420', color: '#ef4444', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 600 }}>Inactive</span>}
                    </div>
                    {ct.description && <p style={{ margin: '0 0 8px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{ct.description}</p>}
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '8px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <span>ID: <code>{ct.case_id_format}</code></span>
                      {ct.schema_json?.fields?.length > 0 && <span>{ct.schema_json.fields.length} field{ct.schema_json.fields.length !== 1 ? 's' : ''}</span>}
                    </div>
                    {ct.status_workflow_json && (
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '10px' }}>
                        {ct.status_workflow_json.statuses?.map(s => (
                          <span key={s.key} style={{ background: `${s.color}20`, color: s.color, padding: '2px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 500 }}>
                            {s.label}
                          </span>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '6px', borderTop: '1px solid var(--border-color)', paddingTop: '10px' }}>
                      <button onClick={() => setEditingType(ct)} style={{ ...btnSecondary, padding: '5px 10px', fontSize: '12px' }}>Edit</button>
                      {ct.is_active && <button onClick={() => handleDelete(ct.id)} style={{ ...btnDanger, padding: '5px 10px' }}>Deactivate</button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Teams tab */}
        {adminTab === 'teams' && <TeamsAdmin workspaceId={workspaceId} />}

        {/* Business Calendars tab */}
        {adminTab === 'calendars' && <BusinessCalendarsAdmin workspaceId={workspaceId} />}

        {/* Decision Tables tab */}
        {adminTab === 'rules' && <DecisionTablesAdmin workspaceId={workspaceId} />}

        {/* Notification Preferences tab */}
        {adminTab === 'notifications' && <NotificationPrefsAdmin workspaceId={workspaceId} />}

        {/* In-Baskets tab (Phase 7) */}
        {adminTab === 'in_baskets' && <InBasketsAdmin workspaceId={workspaceId} getAuthHeaders={getAuthHeaders} />}

        {/* Roles tab */}
        {adminTab === 'roles' && <RolesAdmin workspaceId={workspaceId} getAuthHeaders={getAuthHeaders} />}

        {/* Case Client config tab (Phase 1+ admin UI) */}
        {adminTab === 'case_client' && <CaseClientConfigAdmin workspaceId={workspaceId} getAuthHeaders={getAuthHeaders} />}

        {/* Layouts tab (CASEMATRIX_PRD.md §11) */}
        {adminTab === 'layouts' && (
          <LayoutsList
            token={localStorage.getItem('token')}
            workspaceId={workspaceId}
            currentUser={currentUser}
          />
        )}
      </div>

    </div>
  );
}
